import * as Sentry from '@sentry/browser';
import { isPlaceholderName, isMissingGeneratedCopy, MISSING_COPY_POLL_INTERVAL_MS, MISSING_COPY_MAX_POLLS } from './campaign-poll';
import { PERF_STALE_TIME_MS, PERF_GC_TIME_MS } from './performance/refresh';

import { Query, QueryClient, QueryKey, queryOptions, infiniteQueryOptions, useMutation, useQuery, useQueryClient, keepPreviousData, type InfiniteData } from '@tanstack/react-query';
import {
  connectGoogleService,
  disconnectGoogleService,
  getGoogleIntegrationScopes,
  getGoogleServicesStatus,
  getGoogleSignupScopes,
  getImportChecks,
  registerUserAccount,
  loginUserAccount,
  addOrUpdateCallout,
  addOrUpdatePrice,
  addOrUpdatePromotion,
  addOrUpdateSiteLink,
  addOrUpdateStructuredSnippet,
  updateCampaignRecommendationFix,
  createCampaign,
  getLandingPage,
  getLandingPageLeads,
  getLandingPageEditMessages,
  getCampaignLandingPage,
  getBusinessLandingPages,
  deleteCallout,
  attachCampaignImageExtension,
  detachCampaignImageExtension,
  deletePrice,
  deletePromotion,
  deleteSiteLink,
  deleteStructuredSnippet,
  fetchCampaign,
  fetchCampaignRecommendations,
  fetchCampaignsWithMeta,
  fetchGoogleAdsAccounts,
  fetchGoogleAdsAccountUsers,
  fetchCountries,
  fetchCountryLocations,
  fetchLocations,
  fetchLocationsByIds,
  renameAdGroup,
  setSelectedGoogleAdsAccount,
  updateAd,
  updateAdState,
  updateCampaign,
  updateCampaignState,
  updateGeoTargetingMode,
  updateGoogleCampaign,
  updateAdRecommendationFix,
  updateKeywordRecommendationFix,
  deleteAd,
  deleteCampaign,
  fetchCampaignTaskTracking,
  createAd,
  getAvailableCompetitors,
  fetchNearByLocations,
  deleteCampaignErrors,
  fetchUsers,
  impersonateUser,
  unsetImpersonateUser,
  copyCampaign,
  restartCampaign,
  restartAd,
  fetchGoogleCampaigns,
  importGoogleAdsCampaign,
  linkGoogleAdsCustomer,
  fetchLinkedGoogleAdsAccounts,
  fetchOrganizations,
  createOrganization,
  fetchOrganization,
  saveSetupChecklist,
  fetchBudgetStatus,
  deleteOrganization,
  updateOrganization,
  updateOrganizationCurrency,
  sendInvitationToUser,
  resendOrganizationInvite,
  transferCampaignsToOrganization,
  verifyInviteCode,
  fetchActionLogs,
  fetchAllActionLogs,
  fetchTasksForCampaign,
  undoAction,
  fetchCampaignActionLogs,
  getConversionTrackingSettings,
  createWebsiteConversion,
  createCallConversion,
  listConversionActions,
  removeConversionAction,
  updateConversionAction,
  triggerZeroImpressionOptimisation,
  updateCampaignAllStateApprove,
  createOnboardingCampaign,
  getBusinessByOrganization,
  createBusiness,
  updateBusiness,
  saveBusinessAtomic,
  type BusinessAtomicSavePayload,
  type BusinessUpdateData,
  fetchNegativeKeywordsReport,
  fetchDetectorKeywordsReport,
  generateBusinessDescription,
  createBrandCampaign,
  fetchIsUrlReachable,
  fetchNotificationItems,
  updateNotificationItem,
  bulkUpdateNotifications,
  fetchNotificationCounts,
  fetchInvoices,
  updateCampaignBudget,
  fetchBiddingStrategyReport,
  fetchAccountConversionActionsReport,
  fetchAccountDemographicsReport,
  fetchAccountGeographyReport,
  fetchAccountDeviceReport,
  fetchAccountSearchTermsReport,
  fetchActivityFeed,
  fetchAdPerformanceReport,
  fetchCampaignStatusReport,
  fetchKeywordsOverviewReport,
  fetchLandingPageExperience,
  fetchPerformanceInsights,
  fetchPerformanceReport,
  fetchSearchTermsReport,
  setDeviceBidModifier,
  setLocationBidModifier,
  setScheduleBidModifier,
  getBidModifiers,
  deleteDeviceBidModifier,
  deleteLocationBidModifier,
  deleteScheduleBidModifier,
  listProximityBidModifiers,
  createProximityBidModifier,
  updateProximityBidModifier,
  deleteProximityBidModifier,
  createAdSchedule,
  getAdSchedules,
  getCampaignTrackingTemplate,
  updateAdSchedule,
  deleteAdSchedule,
  getAdNegativeKeywords,
  getCampaignNegativeKeywords,
  addCampaignNegativeKeywords,
  deleteCampaignNegativeKeyword,
  getCampaignIpExclusions,
  addCampaignIpExclusions,
  deleteCampaignIpExclusion,
  getHubSpotIntegrationStatus,
  getHubSpotPipelines,
  saveHubSpotPipelineConfig,
  setupHubSpotConversions,
  getMetaIntegrationStatus,
  selectMetaAdAccount,
  listMetaPagesForAdAccount,
  selectMetaPageForAdAccount,
  listMetaPixelsForAdAccount,
  listMetaLeadgenFormsForAdAccount,
  selectMetaPixelForAdAccount,
  listMetaCampaigns,
  updateMetaAdSetBudget,
  updateMetaCampaignBudget,
  updateMetaCampaignStatus,
  deleteMetaCampaign,
  updateMetaAdCopy,
  updateMetaAdSetName,
  updateMetaAdStatus,
  deleteMetaAd,
  getMetaBudgetRecommendation,
  getMetaConversionEvents,
  getMetaCustomAudiences,
  updateMetaConversionSettings,
  searchMetaInterests,
  searchMetaLocales,
  updateMetaCampaignBidding,
  createMetaAdSet,
  getMetaAdPreview,
  getMetaAdSetTaskStatus,
  updateMetaCampaignBudgetMode,
  updateMetaCampaignTargeting,
  getMetaConversions,
  linkMetaConversion,
  unlinkMetaConversion,
  regenerateMetaAdCopy,
  fetchMetaCampaignEventsHistory,
  getMetaCampaign,
  getMetaCampaignOverview,
  getMetaTimeSeries,
  getMetaCreativePerformance,
  getMetaAdSetPerformance,
  getMetaBudgetPacing,
  getMetaPlacement,
  getMetaCampaignProgress,
  createMetaPhaseCampaign,
  getMetaCampaignTaskStatus,
  getOptimisationConfig,
  getOptimisationHistory,
  getOptimisationOpportunities,
  approveOptimisation,
  dismissOptimisation,
  updateOptimisationConfig,
  getCompetitorTasksByBusiness,
  getCompetitors,
  createCompetitorCampaign,
  fetchOrganizationReports,
  getReportTasksByBusiness,
  getUserNotificationSettings,
  updateUserNotificationSettings,
  getUserSettings,
  updateUserSettings,
  fetchBusinessTaskTracking,
  getCampaignHeadlineMetrics,
  getCampaignDescriptionMetrics,
  generateBrandIntel,
  generateProduct,
  generatePersona,
  fetchExternalCampaigns,
  fetchExternalCampaignDetail,
  fetchBusinessLocations,
  createBusinessLocation,
  updateBusinessLocation,
  dismissBusinessLocation,
  resetBusinessLocationToAgent,
  restoreBusinessLocation,
  confirmBusinessLocation,
  triggerLocationDetect,
  triggerLocationBackfill,
  fetchLocationTaskStatus,
  getConversionActionTrackingSnippet,
  createOptimisationCampaign,
  getCampaignKeywordsWithPerf,
  savePhoneNumber,
  deletePhoneNumber,
  type PhoneNumberPayload,
  getAccountLabels,
  getAdLabels,
  addLabelToAd,
  removeLabelFromAd,
  listAccountSitelinks,
  listCampaignCallouts,
  listAllCampaignIpExclusions,
  listCampaignSitelinks,
  listAccountCallouts,
  listNegativeKeywordLists,
  listNegativeKeywordListKeywords,
  listIpExclusions,
  listApiKeys,
  listOauthConnections,
  revokeOauthConnection,
  getOauthConsentContext,
  refreshGoogleAdsAccountsIfStale,
  checkGoogleAdsForCampaigns,
  fetchReportTypes,
  fetchReportRuns,
  generateEngineReport,
} from './apis';
import {
  Ad,
  AdUpdate,
  CalloutType,
  CAMPAIGN_STATE,
  CampaignData,
  CampaignRecommendations,
  CampaignUpdate,
  BudgetUpdate,
  GoogleAdsAccount,
  Location,
  PriceType,
  PromotionType,
  SiteLink,
  StructuredSnippetType,
  TaskTrackingResponse,
  AD_STATE,
  AllCampaignTaskTrackingResponse,
  ActionFilter,
  PaginationParams,
  NotificationFilter,
  NotificationItem,
  InvitationResponse,
  NotificationResponse,
  InvoiceHistoryResponse,
  BusinessCreate,
  CompetitorCampaignCreate,
  OptimisationConfigUpdate,
  WebsiteConversionCreateRequest,
  CallConversionCreateRequest,
  ConversionActionUpdateRequest,
  UserNotificationSettings,
  UserNotificationSettingsUpdate,
  UserSettings,
  UserSettingsUpdate,
  BusinessTaskTrackingResponse,
  BusinessLocationCreate,
  BusinessLocationUpdate,
  OptimisationCampaignCreatePayload,
  OptimisationCampaignCreateAccepted,
  CampaignKeywordWithPerf,
  MetaPageSelectRequest,
  MetaPixelSelectRequest,
  MetaPhaseCampaignRequest,
  MetaAdCopyUpdateRequest,
  MetaCampaignBiddingUpdateRequest,
  MetaAdSetCreateRequest,
  MetaBudgetModeUpdateRequest,
  MetaCampaignTargetingUpdateRequest,
  MetaConversionSettingsUpdateRequest,
  MetaCampaignGenerationProgress,
  MetaCampaignTaskStatusResponse,
  MetaEntityStatus,
  LocationTargetingMode,
  SetupChecklist,
  GenerateEngineReportRequest,
  GoogleService,
  IntegrationsGoogleServices,
} from './types';
import type {
  OauthConsentParams,
  MetaCampaignOutcome, SyncWarning } from './types';
import { toast, useToast } from '@/hooks/useToast';
import { usePostHog } from 'posthog-js/react';
import { getCurrentGoogleAdsAccountId, getCurrentOrganizationId } from '@/store';
import { getCurrentUserProfile } from './apis';
import { getExcludedLocations, addExcludedLocation, removeExcludedLocation, getCampaignConversionActions } from './apis';
import type { OrganizationChanges } from './apis';
import { isTaskTrackingSummary } from './utils';

// ---- Org/account scope in query keys (#6450) -----------------------------
// Org-scoped list/read caches must be keyed by the active org (and, for Google
// Ads account-scoped data, the selected account) so an org/account switch can
// never serve one scope's rows under another scope's screen. Factories whose
// call sites don't thread the ids read them from the store mirrors: at render
// time for keys, and at request time inside queryFn for explicit
// organization_id params (the backend verifies membership either way and falls
// back only to current_user.last_organization_id, which the awaited
// PUT /organizations/{id}/select keeps in sync — see #6449).
const activeOrgKey = (): number | null => getCurrentOrganizationId() ?? null;
const activeAccountKey = (): string | null => getCurrentGoogleAdsAccountId() ?? null;

// #6489 — activeOrgKey()-keyed queries stay DISABLED until the org mirror is
// seeded. At cold mount the mirror is briefly undefined; a fetch fired in that
// window lands in the `null`-org cache slot and (without an explicit
// organization_id param) is answered for whatever last_organization_id the
// backend remembers — another org's data in a slot every org's cold mount can
// read. Fail closed instead: consumers re-render once the org atom seeds
// (withOrgReset remounts pages; atom subscribers re-render), the key gains the
// real org id, and the query turns on. Combine into `enabled`, never replace.
const orgSeeded = (): boolean => getCurrentOrganizationId() !== undefined;

export const GOOGLE_ADS_ACCOUNTS_QUERY_KEY = ['googleAdsAccounts'] as const;

/** The active organization's Google Analytics and Tag Manager status (#6879). Invalidate with this prefix. */
export const GOOGLE_SERVICES_STATUS_QUERY_KEY = ['google', 'integrations', 'status'] as const;

/**
 * Invalidate the campaign detail cache for a campaign identified by numeric id.
 *
 * The detail query is keyed `['campaign', slug]` (#4876), so a mutation that
 * only knows the numeric id cannot invalidate it directly. A bare `['campaign']`
 * prefix invalidation would work, but it also matches every other cached detail
 * query — including stale ones from previously-viewed orgs, which refetch, 404
 * against the current user's permissions, and revert the UI to its pre-mutation
 * state (#4676). Instead, look up the slug from any cached campaigns list (ids
 * are unique across orgs) and scope the detail invalidation to that one query.
 *
 * The numeric-id prefix invalidation is still issued separately: it matches the
 * id-keyed subqueries such as `['campaign', id, 'headline-metrics', days]`
 * without touching any slug-keyed detail query.
 */
const invalidateCampaignDetailById = (queryClient: QueryClient, campaignId: number) => {
  void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
  const lists = queryClient.getQueriesData<CampaignData[]>({ queryKey: ['campaigns'] });
  for (const [, data] of lists) {
    // getQueriesData matches every ['campaigns', ...]-prefixed cache entry, and
    // not all hold a CampaignData[] (some are paginated/meta objects) — guard
    // before .find so a non-array entry can't throw and abort the restart flow.
    const match = Array.isArray(data) ? data.find(c => c?.id === campaignId) : undefined;
    if (match?.slug) {
      void queryClient.invalidateQueries({ queryKey: ['campaign', match.slug] });
      return;
    }
  }
};

export const useUserAccountRegisterMutation = (name: string, email: string, password: string) => {
  return useMutation({
    mutationKey: ['users', 'register'],
    mutationFn: () => registerUserAccount(name, email, password),

    onSuccess: (data) => {
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUserAccountLoginMutation = (email: string, password: string) => {
  return useMutation({
    mutationKey: ['users', 'register'],
    mutationFn: () => loginUserAccount(email, password),

    onSuccess: (data) => {
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useCreateCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['campaigns', 'create'],
    mutationFn: createCampaign,

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useCreateBusinessMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['businesses', 'create'],
    mutationFn: ({ business, organizationId }: { business: BusinessCreate; organizationId: number }) =>
      createBusiness(business, organizationId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizations', variables.organizationId, 'business'] });
      posthog?.capture('business_created', {
        business_url: data.business_url,
        has_products: Array.isArray(data.products) && data.products.length > 0,
      });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
      posthog?.capture('business_creation_failed', {
        error: error.message,
      });
    },
  });
};

export const useCampaignCopyMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'copy'],
    mutationFn: (name?: string) => copyCampaign(campaignId, name),
    onSuccess: (data) => {
      posthog?.capture('campaign_copied', { campaign_id: campaignId, campaign_slug: data.slug });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.setQueryData(['campaign', data.slug], data);
      return data;
    },
  });
};

export const useUpdateGeoTargetingModeMutation = (slug: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['campaigns', 'geo-targeting-mode', slug],
    mutationFn: ({
      campaignId,
      body,
    }: {
      campaignId: number;
      body: { positive_geo_target_type?: LocationTargetingMode; negative_geo_target_type?: LocationTargetingMode };
    }) => updateGeoTargetingMode(campaignId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', slug] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

export const useCampaignRestartMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'restart', campaignId],
    mutationFn: () => restartCampaign(campaignId),
    onSuccess: (data) => {
      posthog?.capture('campaign_restarted', { campaign_id: campaignId });
      // Invalidate task tracking to show new progress (use prefix match to catch all variations)
      queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
      // Invalidate campaign to reflect any state changes (#4876).
      invalidateCampaignDetailById(queryClient, campaignId);
      return data;
    },
  });
};


export const useAdRestartMutation = (campaignId: number, adId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['ads', 'restart', campaignId, adId],
    mutationFn: () => restartAd(campaignId, adId),
    onSuccess: (data) => {
      posthog?.capture('ad_restarted', { campaign_id: campaignId, ad_id: adId });
      // Invalidate task tracking to show new progress
      queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
      // Invalidate ad task status
      queryClient.invalidateQueries({ queryKey: ['adTaskStatus', adId] });
      // Invalidate campaign to reflect any state changes (#4876).
      invalidateCampaignDetailById(queryClient, campaignId);
      return data;
    },
  });
};

export const useImportGoogleAdsCampaignMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation<
    {
      slug: string;
      campaign_id: string;
    },
    Error,
    { gaCampaignId: string }
  >({
    mutationFn: ({ gaCampaignId }) => importGoogleAdsCampaign(gaCampaignId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      posthog?.capture('google_ads_campaign_imported', { campaign_id: data.campaign_id });
    },
  });
};

export const useGenerateBusinessDescriptionMutation = () => {
  return useMutation({
    mutationKey: ['businesses', 'generate-description'],
    mutationFn: generateBusinessDescription,

    onSuccess: (data) => {
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

/**
 * Query options for fetching competitors by business ID
 */
export const competitorsByBusinessQueryOptions = (businessId: number) => {
  return queryOptions({
    queryKey: ['competitors', businessId],
    queryFn: () => getCompetitors(businessId),
    enabled: !!businessId,
  });
};

/**
 * Query options for polling competitor task status by business ID
 * Automatically polls while there are in-progress tasks
 */
export const competitorTasksQueryOptions = (businessId: number) => {
  return queryOptions({
    queryKey: ['competitor-tasks', businessId],
    queryFn: () => getCompetitorTasksByBusiness(businessId),
    enabled: !!businessId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling if no tasks or no in-progress tasks
      if (!data || !data.has_in_progress) {
        return false;
      }
      // Poll every 10 seconds while tasks are in progress
      return 10000;
    },
  });
};

/**
 * Query options for polling report task status by business ID
 * Automatically polls while there are in-progress tasks
 */
export const reportTasksQueryOptions = (businessId: number) => {
  return queryOptions({
    queryKey: ['report-tasks', businessId],
    queryFn: () => getReportTasksByBusiness(businessId),
    enabled: !!businessId,
    staleTime: 0, // Always consider data stale to get fresh status on navigation
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling if no tasks or no in-progress tasks
      if (!data || !data.has_in_progress) {
        return false;
      }
      // Poll every 10 seconds while tasks are in progress
      return 10000;
    },
  });
};

export const ACTIVE_REPORT_RUNS_POLL_MS = 5000;

export const reportTypesQueryOptions = (organizationId: number) =>
  queryOptions({
    queryKey: ['report-types', organizationId],
    queryFn: () => fetchReportTypes(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

/** The organization's engine runs. The active list polls while anything is queued or running. */
export const reportRunsQueryOptions = (organizationId: number, active?: boolean, idlePollMs?: number) =>
  queryOptions({
    queryKey: ['report-runs', organizationId, active ?? 'all'],
    queryFn: () => fetchReportRuns(organizationId, active),
    enabled: !!organizationId,
    staleTime: 0,
    refetchInterval: (query) =>
      active === true && (query.state.data?.length ?? 0) > 0 ? ACTIVE_REPORT_RUNS_POLL_MS : (idlePollMs ?? false),
  });

export const useGenerateEngineReportMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, request }: { organizationId: number; request: GenerateEngineReportRequest }) =>
      generateEngineReport(organizationId, request),
    onSuccess: (_run, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['report-runs', organizationId] });
    },
  });
};

export const isUrlReachableQueryOptions = (url: string) => {
  return queryOptions({
    queryKey: ['is_url_reachable', url],
    queryFn: async () => {
      if (url === '') return null;
      return await fetchIsUrlReachable(url);
    },
    retry: false,
    enabled: false,
  });
};

export const locationsQueryOptions = (name: string) => {
  return queryOptions({
    queryKey: ['locations', name],
    queryFn: () => fetchLocations(name),
  });
};

export const countriesQueryOptions = () => {
  return queryOptions({
    queryKey: ['locations', 'countries'],
    queryFn: () => fetchCountries(),
    staleTime: 24 * 60 * 60 * 1000, // catalog is static; cache for a day
  });
};

export const countryLocationsQueryOptions = (
  countryCode: string | null,
  parentId: number | null,
  q: string,
) => {
  return queryOptions({
    queryKey: ['locations', 'by-country', countryCode, parentId, q] as const,
    queryFn: () => fetchCountryLocations({ countryCode: countryCode as string, parentId, q }),
    enabled: !!countryCode,
    staleTime: 60 * 60 * 1000,
  });
};

export const nearByLocationsQueryOptions = (location: Location | null) => {
  return queryOptions({
    queryKey: ['nearbyLocations', location],
    queryFn: () => fetchNearByLocations(location),
  });
};

export const locationsByIdsQueryOptions = (criteriaIds: number[]) => {
  // Sort the ids so the queryKey is stable across re-renders that produce
  // the same set in different order. Disabled when there are no ids to
  // look up — saves a no-op fetch.
  const sortedIds = [...criteriaIds].sort((a, b) => a - b);
  return queryOptions({
    queryKey: ['locations-by-ids', sortedIds] as const,
    queryFn: () => fetchLocationsByIds(sortedIds),
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60 * 1000, // canonical_name doesn't change; cache for 5min
  });
};

export const campaignsQueryOptions = (orgId?: number | null, googleAdsCustomerId?: string | null) => {
  return queryOptions({
    queryKey: ['campaigns', orgId, googleAdsCustomerId],
    queryFn: () => fetchCampaignsWithMeta(orgId),
    select: (data) => data.campaigns,
    enabled: !!googleAdsCustomerId && !!orgId,
    // #4760: the campaigns payload is heavy (sidebar needs nested ads/assets). Don't refetch it on
    // every navigation — keep it fresh for 2 min so revisiting /campaigns is instant.
    staleTime: 2 * 60 * 1000,
  });
};

// Campaigns list that INCLUDES external campaigns. Only the /campaigns view
// (sidebar + list page) should use this; every other surface reads the default
// externals-free atom so externals stay scoped to the campaigns view (see #3649).
export const campaignsIncludingExternalQueryOptions = (
  orgId?: number | null,
  includeArchived = false,
  googleAdsCustomerId?: string | null,
) => {
  return queryOptions({
    // #6450: the selected account id joins the key (index 4) so an account
    // switch can't keep serving the previous account's campaign rows.
    queryKey: [
      'campaigns',
      orgId,
      'with-external',
      includeArchived ? 'with-archived' : '',
      googleAdsCustomerId ?? activeAccountKey(),
    ],
    queryFn: () => fetchCampaignsWithMeta(orgId, { includeExternal: true, includeArchived }),
    select: (data) => data.campaigns,
    enabled: !!orgId,
    // #4760: same heavy payload — cache for 2 min so the /campaigns view doesn't refetch every visit.
    staleTime: 2 * 60 * 1000,
  });
};

export const googleCampaignsQueryOptions = () => {
  return queryOptions({
    queryKey: ['googlecampaigns', activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchGoogleCampaigns(),
    enabled: orgSeeded(),
  });
};

// Campaign names containing "Campaign " followed by a 10-char alphanumeric slug
// are placeholders awaiting async AI-generated names from Celery — see
// campaign-poll.ts for the pattern details.
//
// #5066/#6285: while any generated copy is still missing (headlines, or the
// campaign description on a fresh campaign), keep polling the campaign — this
// replaces the ad detail page's bespoke setInterval, whose elapsed-time cap
// reset on every campaign.ads identity change. The cap counts successful
// refetches per query cache entry via dataUpdateCount, so re-renders and
// observer churn cannot reset it. Conditions + constants live in
// campaign-poll.ts (pure, unit-tested); the WeakMap is runtime state.
const missingCopyPollStart = new WeakMap<object, number>();

export const campaignQueryOptions = (slug: string) => {
  return queryOptions({
    queryKey: ['campaign', slug],
    queryFn: () => fetchCampaign(slug),
    // A 404 means the campaign is gone (e.g. just deleted) — retrying stalls the route
    // loader for ~7s of blank screen before its redirect fires (#5665)
    retry: (failureCount, error) =>
      (error as Error & { status?: number }).status !== 404 && failureCount < 3,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const data = query.state.data;
      if (!data?.ads) return 5000;
      if (isPlaceholderName(data.name)) return 3000;
      if (isMissingGeneratedCopy(data)) {
        const start = missingCopyPollStart.get(query) ?? query.state.dataUpdateCount;
        missingCopyPollStart.set(query, start);
        if (query.state.dataUpdateCount - start >= MISSING_COPY_MAX_POLLS) return false;
        return MISSING_COPY_POLL_INTERVAL_MS;
      }
      missingCopyPollStart.delete(query);
      return false;
    },
  });
};

export const importChecksQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: ['importChecks', campaignId],
    queryFn: () => getImportChecks(campaignId),
    // The modal re-runs checks every open: eligibility reflects live Google Ads
    // state, so a cached verdict is worse than a moment's spinner.
    staleTime: 0,
    gcTime: 0,
  });
};

export const campaignRecommendationsQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: ['campaignRecommendations', campaignId],
    queryFn: () => fetchCampaignRecommendations(campaignId) as Promise<CampaignRecommendations | null>,
  });
};

export const useCampaignRecommendationsFixMutationOptions = (campaignId: number, recommendationId: number) => {
  return useMutation({
    mutationKey: ['campaignRecommendationFix', 'apply'],
    mutationFn: () => updateCampaignRecommendationFix(campaignId, recommendationId),
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useAdRecommendationsFixMutationOptions = (campaignId: number, adRecommendationId: number) => {
  return useMutation({
    mutationKey: ['adRecommendationFix', 'apply'],
    mutationFn: () => updateAdRecommendationFix(campaignId, adRecommendationId),
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useKeywordRecommendationsFixMutationOptions = (campaignId: number, keywordRecommendationId: number) => {
  return useMutation({
    mutationKey: ['keywordRecommendationFix', 'apply'],
    mutationFn: () => updateKeywordRecommendationFix(campaignId, keywordRecommendationId),
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useAdStateMutationOptions = (campaignId: number, adId: number, state: 'approved' | 'draft' | 'error') => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['ads', 'state', adId],
    mutationFn: () => updateAdState(campaignId, adId, state),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
      posthog?.capture('ad_state_changed', { campaign_id: campaignId, ad_id: adId, new_state: state });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useAdDeleteMutationOptions = (campaignId: number, adId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['ads', 'delete', campaignId, adId],
    mutationFn: () => deleteAd(campaignId, adId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
      posthog?.capture('ad_deleted', { campaign_id: campaignId, ad_id: adId });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUpdateAdMutationOptions = () => {
  const queryClient = useQueryClient();

  return useMutation<Ad, Error, { campaignId: number; ad: Ad; signal?: AbortSignal }>({
    mutationFn: ({ campaignId, ad, signal }) => {
      // Convert Ad to AdUpdate
      const payload: AdUpdate = {
        id: ad.id,
        url: ad.url,
        slug: ad.slug,
        name: ad.name,
        state: ad.state.toUpperCase() as AD_STATE,
        headlines: ad.headlines.map((headline) => ({
          id: headline.id,
          text: headline.text,
          ad_funnel_stage: headline.ad_funnel_stage,
          // Deletion is explicit: the backend pauses only rows flagged is_deleted and
          // leaves rows merely absent from the payload untouched, so dropping the flag
          // here turns every staged delete into a silent no-op.
          is_deleted: headline.is_deleted,
        })),
        descriptions: ad.descriptions.map((description) => ({
          id: description.id,
          text: description.text,
          ad_funnel_stage: description.ad_funnel_stage,
          is_deleted: description.is_deleted,
        })),
        keywords: ad.keywords.map((keyword) => ({
          id: keyword.id,
          text: keyword.text,
          is_deleted: keyword.is_deleted,
          match_type: keyword.match_type,
          // Carry the pause/lock state: update_keywords re-derives these from the payload, so
          // omitting them defaulted is_paused/is_locked to false server-side and silently un-paused
          // (or unlocked) keywords on every ad save.
          is_paused: keyword.is_paused,
          is_locked: keyword.is_locked,
        })),
        negative_keywords: (ad.negative_keywords || []).map((nk: any) => ({
          id: nk.id ?? null,
          keyword: nk.keyword || nk.text || '',
          match_type: nk.match_type || 'EXACT',
        })),
        labels: (ad.labels || []).map((l) => (l.id ? { id: l.id, name: l.name } : { name: l.name })),
      };
      return updateAd(campaignId, ad.id, payload, { signal });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
  });
};

export const useRenameAdGroupMutationOptions = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; ad_id: number; googleads_ad_group_name: string },
    Error,
    { campaignId: number; adId: number; name: string }
  >({
    mutationFn: ({ campaignId, adId, name }) => renameAdGroup(campaignId, adId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
  });
};

export const useCampaignStateMutationOptions = (campaignId: number, mutationContext?: string) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'state', campaignId, mutationContext],
    mutationFn: (newState: CAMPAIGN_STATE) => updateCampaignState(campaignId, newState),
    onMutate: (newState) => {
      if (newState === CAMPAIGN_STATE.LIVE) {
        posthog?.capture('campaign_launch_attempted', {
          campaign_id: campaignId,
          source: mutationContext,
        });
      }
    },
    onSuccess: async (data, newState) => {
      if (newState === CAMPAIGN_STATE.LIVE) {
        posthog?.capture('campaign_launch_succeeded', {
          campaign_id: campaignId,
          source: mutationContext,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: ['campaigns']
      });
      await queryClient.invalidateQueries({
        queryKey: ['campaign']
      });
      return data;
    },
    onError: (error, variables, context) => {
      posthog?.capture('campaign_launch_failed', {
        campaign_id: campaignId,
        source: mutationContext,
        error: error.message,
      });
      console.error('error', error, variables, context);
    },
  });
};
export const useCampaignDeleteMutationOptions = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'delete', campaignId],
    mutationFn: () => deleteCampaign(campaignId),
    onSuccess: (data) => {
      posthog?.capture('campaign_deleted', { campaign_id: campaignId });
      // Drop the deleted campaign from every cached campaigns list synchronously.
      // Invalidation alone leaves a stale list until the refetch lands, and the
      // /campaigns index redirects to its first entry — bouncing back to the
      // deleted campaign's route and a 404 (#5665).
      queryClient.setQueriesData<CampaignData[]>(
        { queryKey: ['campaigns'] },
        (old) => (Array.isArray(old) ? old.filter((c) => c.id !== campaignId) : old),
      );
      // Invalidate campaigns list to refetch without deleted campaign
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useCampaignMutationOptions = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'update'],
    mutationFn: (campaign: CampaignUpdate) => updateCampaign(campaign),
    onSuccess: (data) => {
      // A live settings save persists locally even when Google rejects part of the push;
      // surface those rejections as error toasts (#5755).
      showSyncWarnings(data);
      posthog?.capture('campaign_info_updated', { campaign_id: data.id });
      // Invalidate campaigns list to refresh sidebar
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      // Update individual campaign cache
      queryClient.setQueryData(['campaign', data.slug], data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useCampaignBudgetMutationOptions = () => {
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['campaigns', 'budget', 'update'],
    mutationFn: ({ campaignId, budgetUpdate }: { campaignId: number; budgetUpdate: BudgetUpdate }) =>
      updateCampaignBudget(campaignId, budgetUpdate),
    onSuccess: (data, variables) => {
      posthog?.capture('campaign_budget_updated', { campaign_id: variables.campaignId });
      // Scope the detail invalidation to this campaign's slug (#4876/#4676).
      invalidateCampaignDetailById(queryClient, variables.campaignId);
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const bidModifiersQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: ['campaigns', campaignId, 'bid-modifiers'],
    queryFn: () => getBidModifiers(campaignId),
    enabled: !!campaignId,
  });
};

export const useDeviceBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'device'],
    mutationFn: (request: Parameters<typeof setDeviceBidModifier>[1]) =>
      setDeviceBidModifier(campaignId, request),
    onSuccess: () => {
      posthog?.capture('bid_modifier_device_set', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
      // #4876: the detail query is keyed by slug, not numeric id.
      invalidateCampaignDetailById(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error setting device bid modifier', error, variables, context);
    },
  });
};

export const useLocationBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'location'],
    mutationFn: (request: Parameters<typeof setLocationBidModifier>[1]) =>
      setLocationBidModifier(campaignId, request),
    onSuccess: () => {
      posthog?.capture('bid_modifier_location_set', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
      // #4876: the detail query is keyed by slug, not numeric id.
      invalidateCampaignDetailById(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error setting location bid modifier', error, variables, context);
    },
  });
};

export const useScheduleBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'schedule'],
    mutationFn: (request: Parameters<typeof setScheduleBidModifier>[1]) =>
      setScheduleBidModifier(campaignId, request),
    onSuccess: () => {
      posthog?.capture('bid_modifier_schedule_set', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ad-schedules'] });
      // #4876: the detail query is keyed by slug, not numeric id.
      invalidateCampaignDetailById(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error setting schedule bid modifier', error, variables, context);
    },
  });
};

export const useDeleteDeviceBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'device', 'delete'],
    mutationFn: (criterionId: string) => deleteDeviceBidModifier(campaignId, criterionId),
    onSuccess: () => {
      posthog?.capture('bid_modifier_device_deleted', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
      // #4876: the detail query is keyed by slug, not numeric id.
      invalidateCampaignDetailById(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error deleting device bid modifier', error, variables, context);
    },
  });
};

export const useDeleteLocationBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'location', 'delete'],
    mutationFn: (criterionId: string) => deleteLocationBidModifier(campaignId, criterionId),
    onSuccess: () => {
      posthog?.capture('bid_modifier_location_deleted', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
      // #4876: the detail query is keyed by slug, not numeric id.
      invalidateCampaignDetailById(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error deleting location bid modifier', error, variables, context);
    },
  });
};

export const useDeleteScheduleBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'schedule', 'delete'],
    mutationFn: (criterionId: string) => deleteScheduleBidModifier(campaignId, criterionId),
    onSuccess: () => {
      posthog?.capture('bid_modifier_schedule_deleted', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ad-schedules'] });
      // #4876: the detail query is keyed by slug, not numeric id.
      invalidateCampaignDetailById(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error deleting schedule bid modifier', error, variables, context);
    },
  });
};

// Proximity bid modifiers (#4384) ---------------------------------------------

const proximityModifiersKey = (campaignId: number) =>
  ['campaigns', campaignId, 'bid-modifiers', 'proximity'] as const;

export const proximityBidModifiersQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: proximityModifiersKey(campaignId),
    queryFn: () => listProximityBidModifiers(campaignId),
    enabled: !!campaignId,
  });
};

const invalidateProximityCaches = (queryClient: ReturnType<typeof useQueryClient>, campaignId: number) => {
  queryClient.invalidateQueries({ queryKey: proximityModifiersKey(campaignId) });
  queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
  queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
};

export const useCreateProximityBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'proximity', 'create'],
    mutationFn: (request: Parameters<typeof createProximityBidModifier>[1]) =>
      createProximityBidModifier(campaignId, request),
    onSuccess: () => {
      posthog?.capture('bid_modifier_proximity_set', { campaign_id: campaignId });
      invalidateProximityCaches(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error creating proximity bid modifier', error, variables, context);
    },
  });
};

export const useUpdateProximityBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'proximity', 'update'],
    mutationFn: ({ modifierId, request }: {
      modifierId: number;
      request: Parameters<typeof updateProximityBidModifier>[2];
    }) => updateProximityBidModifier(campaignId, modifierId, request),
    onSuccess: () => {
      posthog?.capture('bid_modifier_proximity_updated', { campaign_id: campaignId });
      invalidateProximityCaches(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error updating proximity bid modifier', error, variables, context);
    },
  });
};

export const useDeleteProximityBidModifierMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'bid-modifiers', 'proximity', 'delete'],
    mutationFn: (modifierId: number) => deleteProximityBidModifier(campaignId, modifierId),
    onSuccess: () => {
      posthog?.capture('bid_modifier_proximity_deleted', { campaign_id: campaignId });
      invalidateProximityCaches(queryClient, campaignId);
    },
    onError: (error, variables, context) => {
      console.error('error deleting proximity bid modifier', error, variables, context);
    },
  });
};

// Ad Schedule (Day Parting) Query Options
export const adSchedulesQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: ['campaigns', campaignId, 'ad-schedules'],
    queryFn: () => getAdSchedules(campaignId),
  });
};

export const useAdSchedulesQuery = (campaignId: number) => {
  return useQuery(adSchedulesQueryOptions(campaignId));
};

// Tracking Template / UTM Parameters (#4379, read-only)
export const trackingTemplateQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: ['campaigns', campaignId, 'tracking-template'],
    queryFn: () => getCampaignTrackingTemplate(campaignId),
    enabled: !!campaignId,
  });
};

export const useCampaignTrackingTemplateQuery = (campaignId: number) => {
  return useQuery(trackingTemplateQueryOptions(campaignId));
};

export const useCreateAdScheduleMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'ad-schedules', 'create'],
    mutationFn: (schedule: Parameters<typeof createAdSchedule>[1]) => createAdSchedule(campaignId, schedule),
    onSuccess: () => {
      posthog?.capture('ad_schedule_created', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ad-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
    },
    onError: (error, variables, context) => {
      console.error('error creating ad schedule', error, variables, context);
    },
  });
};

export const useUpdateAdScheduleMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'ad-schedules', 'update'],
    mutationFn: ({ scheduleId, schedule }: { scheduleId: number; schedule: Parameters<typeof updateAdSchedule>[2] }) =>
      updateAdSchedule(campaignId, scheduleId, schedule),
    onSuccess: () => {
      posthog?.capture('ad_schedule_updated', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ad-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
    },
    onError: (error, variables, context) => {
      console.error('error updating ad schedule', error, variables, context);
    },
  });
};

export const useDeleteAdScheduleMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'ad-schedules', 'delete'],
    mutationFn: (scheduleId: number) => deleteAdSchedule(campaignId, scheduleId),
    onSuccess: () => {
      posthog?.capture('ad_schedule_deleted', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ad-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'bid-modifiers'] });
    },
    onError: (error, variables, context) => {
      console.error('error deleting ad schedule', error, variables, context);
    },
  });
};

// Ad-level Negative Keywords Query Hooks
export const adNegativeKeywordsQueryOptions = (campaignId: number, adId: number, includePaused: boolean = false) => {
  return queryOptions({
    queryKey: ['campaigns', campaignId, 'ads', adId, 'negative-keywords', { includePaused }],
    queryFn: () => getAdNegativeKeywords(campaignId, adId, includePaused),
    enabled: !!campaignId && !!adId,
  });
};

export const useAdNegativeKeywordsQuery = (campaignId: number, adId: number, includePaused: boolean = false) => {
  return useQuery(adNegativeKeywordsQueryOptions(campaignId, adId, includePaused));
};

// Campaign Negative Keywords Query Hooks
export const campaignNegativeKeywordsQueryOptions = (campaignId: number, includePaused: boolean = false) => {
  return queryOptions({
    queryKey: ['campaigns', campaignId, 'negative-keywords', { includePaused }],
    queryFn: () => getCampaignNegativeKeywords(campaignId, includePaused),
  });
};

export const useCampaignNegativeKeywordsQuery = (campaignId: number, includePaused: boolean = false) => {
  return useQuery(campaignNegativeKeywordsQueryOptions(campaignId, includePaused));
};

export const useAddCampaignNegativeKeywordsMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'negative-keywords', 'add'],
    mutationFn: (keywords: Parameters<typeof addCampaignNegativeKeywords>[1]) =>
      addCampaignNegativeKeywords(campaignId, keywords),
    onSuccess: () => {
      posthog?.capture('negative_keywords_added', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'negative-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ads'] });
    },
    onError: (error, variables, context) => {
      console.error('error adding campaign negative keywords', error, variables, context);
    },
  });
};

export const useDeleteCampaignNegativeKeywordMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'negative-keywords', 'delete'],
    mutationFn: (keywordId: number) => deleteCampaignNegativeKeyword(campaignId, keywordId),
    onSuccess: () => {
      posthog?.capture('negative_keyword_deleted', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'negative-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ads'] });
    },
    onError: (error, variables, context) => {
      console.error('error deleting campaign negative keyword', error, variables, context);
    },
  });
};

export const campaignIpExclusionsQueryOptions = (campaignId: number) => {
  return queryOptions({
    queryKey: ['campaigns', campaignId, 'ip-exclusions'],
    queryFn: () => getCampaignIpExclusions(campaignId),
  });
};

export const useCampaignIpExclusionsQuery = (campaignId: number) => {
  return useQuery(campaignIpExclusionsQueryOptions(campaignId));
};

export const useAddCampaignIpExclusionsMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'ip-exclusions', 'add'],
    mutationFn: (ipAddresses: string[]) => addCampaignIpExclusions(campaignId, ipAddresses),
    onSuccess: () => {
      posthog?.capture('campaign_ip_exclusions_added', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ip-exclusions'] });
    },
    onError: (error, variables, context) => {
      console.error('error adding campaign IP exclusions', error, variables, context);
    },
  });
};

export const useDeleteCampaignIpExclusionMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', campaignId, 'ip-exclusions', 'delete'],
    mutationFn: (exclusionId: number) => deleteCampaignIpExclusion(campaignId, exclusionId),
    onSuccess: () => {
      posthog?.capture('campaign_ip_exclusion_deleted', { campaign_id: campaignId });
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'ip-exclusions'] });
    },
    onError: (error, variables, context) => {
      console.error('error deleting campaign IP exclusion', error, variables, context);
    },
  });
};

// Account-level Google Ads assets (#5351) ------------------------------------
//
// The four "Google account asset" sections — Sitelinks, Callouts, Negative
// Keyword Lists and IP Exclusions — share one caching policy: a 5-minute stale
// time and the selected Google Ads account id baked into every query key, so
// switching account auto-refetches. Previously each section re-invalidated by
// hand in a useEffect; Negative Keywords was missing that effect entirely, so
// its data went stale across account switches. The list endpoints read the
// selected account server-side, so the id is a cache key only, not a fetch arg.
export const ACCOUNT_ASSET_STALE_TIME = 5 * 60 * 1000;

export const accountSitelinksQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['account-sitelinks', accountId],
    queryFn: listAccountSitelinks,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

// The campaign half of the same view (#6745): the sitelinks set on campaigns,
// each with the campaigns carrying it.
export const campaignSitelinksQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['campaign-sitelinks', accountId],
    queryFn: listCampaignSitelinks,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

export const campaignCalloutsQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['campaign-callouts', accountId],
    queryFn: listCampaignCallouts,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

// Every campaign's IP exclusions, rolled up for the Assets view. The
// per-campaign campaignIpExclusionsQueryOptions(campaignId) further down is a
// different thing: one campaign's own rows.
export const allCampaignIpExclusionsQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['all-campaign-ip-exclusions', accountId],
    queryFn: listAllCampaignIpExclusions,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

export const accountCalloutsQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['account-callouts', accountId],
    queryFn: listAccountCallouts,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

export const negativeKeywordListsQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['negative-keyword-lists', accountId],
    queryFn: listNegativeKeywordLists,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

export const negativeKeywordListKeywordsQueryOptions = (
  accountId: string | null | undefined,
  listId: string | null,
) =>
  queryOptions({
    queryKey: ['negative-keyword-list', accountId, listId, 'keywords'],
    queryFn: () => listNegativeKeywordListKeywords(listId!),
    enabled: !!accountId && !!listId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

export const accountIpExclusionsQueryOptions = (accountId?: string | null) =>
  queryOptions({
    queryKey: ['account-ip-exclusions', accountId],
    queryFn: listIpExclusions,
    enabled: !!accountId,
    staleTime: ACCOUNT_ASSET_STALE_TIME,
  });

export const useCampaignAllStateApproveMutationOptions = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaign', 'all-state', campaignSlug],
    mutationFn: () => updateCampaignAllStateApprove(campaignSlug),
    onSuccess: async (data) => {
      posthog?.capture('campaign_states_approved', { campaign_slug: campaignSlug });
      // Invalidate the campaign query to trigger a refetch
      await queryClient.invalidateQueries({ queryKey: ['campaign', campaignSlug] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUpdateGoogleCampaignMutationOptions = (campaignId: number) => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['googleCampaign', 'update'],
    mutationFn: () => updateGoogleCampaign(campaignId),
    onSuccess: () => {
      posthog?.capture('google_campaign_synced', { campaign_id: campaignId });
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

// Live-sync rejections (#5682): the save succeeds but Google may reject individual
// assets during the re-push. Surface each rejection as an error toast.
const showSyncWarnings = (data: unknown) => {
  const warnings = (data as { sync_warnings?: SyncWarning[] } | null | undefined)?.sync_warnings;
  warnings?.forEach((warning) => toast({ description: warning.message, variant: 'destructive' }));
};

export const useUpdateSiteLinkMutationOptions = () => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['siteLinks', 'update'],
    mutationFn: ({ campaignId, siteLink }: { campaignId: number; siteLink: SiteLink }) =>
      addOrUpdateSiteLink(campaignId, siteLink),
    onSuccess: (data, variables) => {
      showSyncWarnings(data);
      posthog?.capture('sitelink_updated', { campaign_id: variables.campaignId });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useDeleteSiteLinkMutationOptions = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['siteLinks', 'delete'],
    mutationFn: ({ campaignId, siteLinkId }: { campaignId: number; siteLinkId: number }) =>
      deleteSiteLink(campaignId, siteLinkId),
    onSuccess: async (data, variables) => {
      showSyncWarnings(data);
      posthog?.capture('sitelink_deleted', { campaign_id: variables.campaignId });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useCampaignErrorsDeleteMutationOptions = (campaignId: number) => {
  return useMutation({
    mutationKey: ['campaignsErrors', 'delete', campaignId],
    mutationFn: () => deleteCampaignErrors(campaignId),
    onSuccess: (data) => {
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

/**
 * Competitors on a competitor campaign that do not have an ad yet. Feeds the Add Ad picker.
 * Empty for other campaign types, so the modal falls back to the landing page combobox.
 */
export const availableCompetitorsQueryOptions = (campaignId: number, enabled = true) => {
  return queryOptions({
    queryKey: ['available-competitors', campaignId],
    queryFn: () => getAvailableCompetitors(campaignId),
    enabled: enabled && !!campaignId,
    staleTime: 30 * 1000,
  });
};

export const useCreateAdMutationOptions = () => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['ads', 'create'],
    mutationFn: ({ campaignId, url, competitorId }: { campaignId: number; url: string; competitorId?: string }) =>
      createAd(campaignId, url, competitorId),
    onSuccess: (_, variables) => {
      posthog?.capture('ad_created', { campaign_id: variables.campaignId, competitor_id: variables.competitorId });
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};
export const useUpdateCalloutMutationOptions = () => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['callouts', 'update'],
    mutationFn: ({ campaignId, callout }: { campaignId: number; callout: CalloutType }) =>
      addOrUpdateCallout(campaignId, callout),
    onSuccess: (data, variables) => {
      showSyncWarnings(data);
      posthog?.capture('callout_updated', { campaign_id: variables.campaignId });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useDeleteCalloutMutationOptions = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['callouts', 'delete'],
    mutationFn: ({ campaignId, calloutId }: { campaignId: number; calloutId: number }) =>
      deleteCallout(campaignId, calloutId),
    onSuccess: async (data, variables) => {
      showSyncWarnings(data);
      posthog?.capture('callout_deleted', { campaign_id: variables.campaignId });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useSavePhoneNumberMutation = () => {
  return useMutation({
    mutationKey: ['phoneNumbers', 'save'],
    mutationFn: ({ campaignId, phoneNumber }: { campaignId: number; phoneNumber: PhoneNumberPayload }) =>
      savePhoneNumber(campaignId, phoneNumber),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error) => {
      console.error('error saving phone number', error);
    },
  });
};

export const useDeletePhoneNumberMutation = () => {
  return useMutation({
    mutationKey: ['phoneNumbers', 'delete'],
    mutationFn: ({ campaignId, phoneNumberId }: { campaignId: number; phoneNumberId: number }) =>
      deletePhoneNumber(campaignId, phoneNumberId),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error) => {
      console.error('error deleting phone number', error);
    },
  });
};

// Image extensions (#4376): attach/detach a creative-asset image to a campaign
export const useAttachImageExtensionMutationOptions = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['imageExtensions', 'attach'],
    mutationFn: ({ campaignId, creativeAssetId }: { campaignId: number; creativeAssetId: number }) =>
      attachCampaignImageExtension(campaignId, creativeAssetId),
    onSuccess: async (data, variables) => {
      showSyncWarnings(data);
      posthog?.capture('image_extension_attached', { campaign_id: variables.campaignId });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useDetachImageExtensionMutationOptions = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['imageExtensions', 'detach'],
    mutationFn: ({ campaignId, creativeAssetId }: { campaignId: number; creativeAssetId: number }) =>
      detachCampaignImageExtension(campaignId, creativeAssetId),
    onSuccess: async (data, variables) => {
      showSyncWarnings(data);
      posthog?.capture('image_extension_detached', { campaign_id: variables.campaignId });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUpdateStructuredSnippetMutationOptions = () => {
  return useMutation({
    mutationKey: ['structuredSnippets', 'update'],
    mutationFn: ({ campaignId, structuredSnippet }: { campaignId: number; structuredSnippet: StructuredSnippetType }) =>
      addOrUpdateStructuredSnippet(campaignId, structuredSnippet),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useDeleteStructuredSnippetMutationOptions = () => {
  return useMutation({
    mutationKey: ['structuredSnippets', 'delete'],
    mutationFn: ({ campaignId, structuredSnippetId }: { campaignId: number; structuredSnippetId: number }) =>
      deleteStructuredSnippet(campaignId, structuredSnippetId),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUpdatePriceMutationOptions = () => {
  return useMutation({
    mutationKey: ['prices', 'update'],
    mutationFn: ({ campaignId, price }: { campaignId: number; price: PriceType }) =>
      addOrUpdatePrice(campaignId, price),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useDeletePriceMutationOptions = () => {
  return useMutation({
    mutationKey: ['prices', 'delete'],
    mutationFn: ({ campaignId, priceId }: { campaignId: number; priceId: number }) => deletePrice(campaignId, priceId),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUpdatePromotionMutationOptions = () => {
  return useMutation({
    mutationKey: ['promotions', 'update'],
    mutationFn: ({ campaignId, promotion }: { campaignId: number; promotion: PromotionType }) =>
      addOrUpdatePromotion(campaignId, promotion),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useDeletePromotionMutationOptions = () => {
  return useMutation({
    mutationKey: ['promotions', 'delete'],
    mutationFn: ({ campaignId, promotionId }: { campaignId: number; promotionId: number }) =>
      deletePromotion(campaignId, promotionId),
    onSuccess: (data) => {
      showSyncWarnings(data);
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const googleAdsAccountsQueryOptions = () => {
  return queryOptions({
    // Exported so no caller hard-codes the literal again (#4913 — the key was
    // misspelled 'goolgeadsAccounts' and copy-pasted as raw strings).
    queryKey: GOOGLE_ADS_ACCOUNTS_QUERY_KEY,
    queryFn: () => fetchGoogleAdsAccounts(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const googleAdsLinkedAccountsQueryOptions = () => {
  return queryOptions({
    queryKey: ['googleAdsLinkedAccounts'],
    queryFn: () => fetchLinkedGoogleAdsAccounts(),
    refetchInterval: (query: Query<GoogleAdsAccount[], Error, GoogleAdsAccount[], QueryKey>) => {
      if (query.state.status === 'error') return false;
      const data = query.state.data;
      if (data && data.length > 0) {
        return false;
      }

      return 2000;
    },
    // Inherit the global retry policy (max 3, none on session expiry) — the old
    // `retry: 5` here amplified the auth-expiry refetch storm (#5573).
  });
};

export const linkGoogleAdsCustomertMutationOptions = () => {
  return useMutation({
    mutationKey: ['googleAdsCustomer', 'link'],
    mutationFn: ({ googleAdsCustomerId, name }: { googleAdsCustomerId: number; name: string }) =>
      linkGoogleAdsCustomer(googleAdsCustomerId, name),
    onSuccess: (data) => {
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useSetGoogleAdsCustomerIdMutationOptions = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['googleAdsCustomerId'],
    mutationFn: ({ googleAdsAccountId, organizationId }: { googleAdsAccountId: string; organizationId?: number }) =>
      setSelectedGoogleAdsAccount(googleAdsAccountId, organizationId),
    onSuccess: (data, variables) => {
      posthog?.capture('google_ads_account_selected', { account_id: variables.googleAdsAccountId });
      // Invalidate the accounts query to refetch with updated selection state
      queryClient.invalidateQueries({ queryKey: GOOGLE_ADS_ACCOUNTS_QUERY_KEY });
      // Also refetch organizations so membership.last_selected_googleads_account_*
      // updates everywhere (org settings status, top-bar) without a hard refresh. #4273
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      // #6450: account-scoped caches must not survive an account switch —
      // invalidate them centrally here instead of relying on each call site.
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['googlecampaigns'] });
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      queryClient.invalidateQueries({ queryKey: ['conversion-tracking'] });
      queryClient.invalidateQueries({ queryKey: ['conversion-tracking-snippet'] });
      // The merged activity feed's Google half is account-scoped (#6489).
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

/** Check the organization's selected Google Ads account for campaigns to list (#6612). Listed campaigns and
 * corrected live or paused states land in the campaigns lists, so those refetch after every check. */
export const useCheckGoogleAdsForCampaignsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['campaigns', 'check-google-ads'],
    mutationFn: (organizationId: number) => checkGoogleAdsForCampaigns(organizationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

export const useSilentRefreshGoogleAdsAccountsMutationOptions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['googleAdsAccountsSilentRefresh'],
    mutationFn: () => refreshGoogleAdsAccountsIfStale(),
    onSuccess: (data) => {
      // Only a real re-enumeration can change the stored list; skips (fresh list, no
      // token, broken connection) leave it untouched, so nothing to refetch.
      if (data.refreshed) {
        queryClient.invalidateQueries({ queryKey: GOOGLE_ADS_ACCOUNTS_QUERY_KEY });
      }
    },
    onError: (error) => {
      // Silent by design (#4207): the modal must not toast or open OAuth when the
      // background refresh fails. The explicit refresh button remains the loud path.
      console.error('Silent Google Ads account refresh failed:', error);
    },
  });
};

export const campaignTaskTrackingQueryOptions = (campaignId: number, adCreation?: string) => {
  let confirmationPolls = 0;

  return queryOptions<TaskTrackingResponse, Error>({
    queryKey: ['campaignTaskTracking', adCreation, campaignId],
    queryFn: () => fetchCampaignTaskTracking(campaignId),
    refetchInterval: ({ state: { data: trackings, status } }) => {
      if (status === 'error') return false;
      // If no data yet, keep polling
      if (!trackings) return 3000;

      // Check if campaign is marked as stale by the backend
      if (trackings.is_stale) {
        // For stale campaigns (older than 1 hour), check for in-progress tasks

        const hasInProgressTasks = Object.values(trackings).some(
          (value) => isTaskTrackingSummary(value) && value.in_progress > 0,
        );

        // Only poll every 30 seconds for stale campaigns with tasks in progress
        return hasInProgressTasks ? 3000 : false;
      }

      const { sumTotal, taskProcessing } = Object.values(trackings).reduce(
        (acc, value) => {
          // Skip metadata fields
          if (isTaskTrackingSummary(value)) {
            acc.sumTotal += value.total;
            acc.taskProcessing += value.in_progress;
          }
          return acc;
        },
        { sumTotal: 0, taskProcessing: 0 },
      );

      if (taskProcessing > 0 || sumTotal === 0) {
        confirmationPolls = 0;
        return 3000;
      }

      // Tasks appear done — do 2 more confirmation polls to ensure state is stable
      confirmationPolls++;
      return confirmationPolls <= 2 ? 3000 : false;
    },
  });
};

// Reverse-sync cache invalidation (#5745). Implementation lives in the sibling leaf module
// `reverse-sync-invalidation.ts` (type-only imports) so it can be unit-tested under bun test
// without loading this module's heavy graph. Re-exported here so the documented import path
// `@/lib/query-options` keeps working alongside invalidateProximityCaches / invalidateBusinessLocationConsumers.
export { invalidateCampaignReverseSync } from './reverse-sync-invalidation';

// NOTE: We're no longer using this function as we've optimized to only poll
// for the specific campaign being viewed or generated.
// Keep the code commented for reference in case it's needed in the future.

export const allCampaignTaskTrackingQueryOptions = (campaignIds: number[]) => {
  return queryOptions<AllCampaignTaskTrackingResponse, Error>({
    queryKey: ['allCampaignTaskTracking', campaignIds],

    queryFn: async () => {
      const fetchWithRetry = async (id: number, retries = 3, delay = 1000): Promise<TaskTrackingResponse> => {
        try {
          return await fetchCampaignTaskTracking(id);
        } catch (error) {
          if (retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
            return fetchWithRetry(id, retries - 1, delay * 2); // Exponential backoff
          }
          throw error; // If all retries fail, throw the error
        }
      };

      const results = await Promise.all(campaignIds.map((id) => fetchWithRetry(id)));

      return results.reduce<AllCampaignTaskTrackingResponse>((acc, cur, idx) => {
        const id = campaignIds[idx];
        acc[id] = cur;
        return acc;
      }, {});
    },

    refetchInterval: ({ state: { data: allTrackings, status } }) => {
      if (status === 'error') return false;
      if (!allTrackings) return 1000;

      // Check for fresh and stale campaigns separately
      const freshCampaigns: TaskTrackingResponse[] = [];
      const staleCampaigns: TaskTrackingResponse[] = [];

      // Categorize campaigns
      Object.values(allTrackings).forEach((tracking) => {
        if (tracking.is_stale) {
          staleCampaigns.push(tracking);
        } else {
          freshCampaigns.push(tracking);
        }
      });

      // Process stale campaigns - only poll if they have in-progress tasks
      const staleHasInProgressTasks = staleCampaigns.some((tracking) =>
        Object.values(tracking).some(
          (summary) => typeof summary === 'object' && 'in_progress' in summary && summary.in_progress > 0,
        ),
      );

      const { sumTotal, taskProcessing } = Object.values(freshCampaigns).reduce(
        (acc, cur) => {
          return {
            sumTotal:
              acc.sumTotal +
              Object.values(cur).reduce((innerAcc, innerCur) => {
                return isTaskTrackingSummary(innerCur) ? innerAcc + innerCur.total : innerAcc;
              }, 0),
            taskProcessing:
              acc.taskProcessing +
              Object.values(cur).reduce((innerAcc, innerCur) => {
                return isTaskTrackingSummary(innerCur) ? innerAcc + innerCur.in_progress : innerAcc;
              }, 0),
          };
        },
        { sumTotal: 0, taskProcessing: 0 },
      );

      // Fresh campaigns: poll every 3s if tasks are in progress
      // Stale campaigns: poll every 30s if tasks are in progress
      // No campaigns or all done: stop polling

      if (freshCampaigns.length > 0 && (sumTotal === 0 || taskProcessing > 0)) {
        return 3000; // Poll fresh campaigns every 3 seconds
      } else if (staleHasInProgressTasks) {
        return 30000; // Poll stale campaigns with in-progress tasks every 30 seconds
      } else {
        return false; // Stop polling if all campaigns are done
      }
    },
  });
};

export const usersQueryOptions = () => {
  return queryOptions({
    queryKey: ['users'],
    queryFn: () => fetchUsers(),
  });
};

export const useImpersonateUserMutationOptions = () => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['impersonate'],
    mutationFn: (email: string) => impersonateUser(email),
    onSuccess: (data) => {
      posthog?.capture('user_impersonated');
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useUnsetImpersonateUserMutationOptions = () => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['unset_impersonate'],
    mutationFn: () => unsetImpersonateUser(),
    onSuccess: (data) => {
      posthog?.capture('impersonation_ended');
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

/** The sign-up popup's scope (#6879). Loaded by the routes that render the Google sign-up button, so the popup is
 * configured before anyone can click it. */
export const googleSignupScopesQueryOptions = () =>
  queryOptions({
    queryKey: ['google', 'consent-scopes', 'signup'],
    queryFn: getGoogleSignupScopes,
    staleTime: 5 * 60 * 1000,
  });

/** Fast polling while a first listing runs, then slower; no polling once it reported a problem or errored. */
const GOOGLE_SERVICES_FAST_POLLS = 20;

export const googleServicesStatusRefetchInterval = (state: {
  data?: IntegrationsGoogleServices;
  error: unknown;
  dataUpdateCount: number;
}): number | false => {
  if (state.error || !state.data) return false;
  const firstListingRunning = state.data.services.some((service) => service.state === 'checking' && !service.problem);
  if (!firstListingRunning) return false;
  return state.dataUpdateCount < GOOGLE_SERVICES_FAST_POLLS ? 3000 : 30000;
};

/** Google Analytics and Tag Manager for Settings → Integrations (#6879). Polls while a first listing runs. */
export const googleServicesStatusQueryOptions = (analyticsTagManagerEnabled: boolean) =>
  queryOptions<IntegrationsGoogleServices, Error>({
    queryKey: [...GOOGLE_SERVICES_STATUS_QUERY_KEY, activeOrgKey()],
    queryFn: getGoogleServicesStatus,
    staleTime: 30_000,
    enabled: analyticsTagManagerEnabled && orgSeeded(),
    refetchInterval: (query) => googleServicesStatusRefetchInterval(query.state),
  });

export const useConnectGoogleServiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ service, code }: { service: GoogleService; code: string }) => connectGoogleService(service, code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GOOGLE_SERVICES_STATUS_QUERY_KEY }),
  });
};

export const useDisconnectGoogleServiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (service: GoogleService) => disconnectGoogleService(service),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GOOGLE_SERVICES_STATUS_QUERY_KEY }),
  });
};

/** The Integrations connect popups' scopes for this user (#6879). Loaded with the authenticated shell. */
export const googleIntegrationScopesQueryOptions = () =>
  queryOptions({
    queryKey: ['google', 'consent-scopes', 'integrations'],
    queryFn: getGoogleIntegrationScopes,
    staleTime: 5 * 60 * 1000,
  });

export const userProfileQueryOptions = () => {
  return queryOptions({
    queryKey: ['user', 'profile'],
    queryFn: () => getCurrentUserProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount) => {
      return failureCount < 2; // Only retry twice
    },
    retryDelay: (attemptIndex) => {
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
  });
};

export const organizationsQueryOptions = () => {
  return queryOptions({
    queryKey: ['organizations'],
    queryFn: () => fetchOrganizations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount) => {
      return failureCount < 2; // Only retry twice
    },
    retryDelay: (attemptIndex) => {
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
  });
};

export const useCreateOrganizationMutation = () => {
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['organizations', 'create'],
    mutationFn: createOrganization,

    onSuccess: (data) => {
      posthog?.capture('organization_created', { organization_id: data.id });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const organizationQueryOptions = (organizationId: number) => {
  return queryOptions({
    queryKey: ['organizations', organizationId],
    queryFn: () => fetchOrganization(organizationId),
    refetchInterval: () => {
      return false;
    },
  });
};

// The Setup page's ticks (#6708): the organisation keeps them, so the page
// sends the whole list on every change and the answer is what was saved.
export const useSaveSetupChecklistMutation = (organizationId: number) => {
  return useMutation({
    mutationKey: ['organizations', organizationId, 'setupChecklist'],
    mutationFn: (checklist: SetupChecklist) => saveSetupChecklist(organizationId, checklist),
  });
};

export const budgetStatusQueryOptions = (organizationId: number | undefined) => {
  return queryOptions({
    queryKey: ['organizations', organizationId, 'budgetStatus'],
    queryFn: () => fetchBudgetStatus(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
};

export const businessQueryOptions = (organizationId: number) => {
  return queryOptions({
    queryKey: ['organizations', organizationId, 'business'],
    queryFn: () => getBusinessByOrganization(organizationId),
    enabled: !!organizationId,
  });
};

export const businessTaskTrackingQueryOptions = (businessId: number | undefined) => {
  return queryOptions<BusinessTaskTrackingResponse, Error>({
    queryKey: ['businessTaskTracking', businessId],
    queryFn: () => fetchBusinessTaskTracking(businessId!),
    enabled: !!businessId,
    refetchInterval: ({ state: { data, status } }) => {
      if (status === 'error') return false;
      if (!data) return 3000;
      return data.is_generating ? 3000 : false;
    },
  });
};

export const useUpdateBusinessMutation = (organizationId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['businesses', 'update'],
    mutationFn: ({ businessId, data }: { businessId: number; data: BusinessUpdateData }) =>
      updateBusiness(businessId, data),
    onSuccess: () => {
      posthog?.capture('business_profile_updated', { organization_id: organizationId });
      // Invalidate businesses query to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'business'] });
    },
    onError: (error) => {
      console.error('Failed to update business:', error);
    },
  });
};

/**
 * Atomic business "Save Changes" (#5401). Persists core fields + KPI targets + locations in one
 * transaction. On success, invalidates the business query so the page's dirty-state effects reset;
 * the KPI/locations sections adopt their saved baselines from the response (via their save
 * controllers). Budget is saved separately.
 */
export const useSaveBusinessMutation = (organizationId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['businesses', 'atomic-save'],
    mutationFn: ({ businessId, payload }: { businessId: number; payload: BusinessAtomicSavePayload }) =>
      saveBusinessAtomic(businessId, payload),
    onSuccess: () => {
      posthog?.capture('business_profile_updated', { organization_id: organizationId });
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'business'] });
    },
    onError: (error) => {
      console.error('Failed to save business:', error);
    },
  });
};

export const useGenerateBrandIntelMutation = (organizationId: number, businessId?: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ['businesses', 'generate-brand-intel'],
    mutationFn: (bizId: number) => generateBrandIntel(bizId),
    onSuccess: () => {
      posthog?.capture('brand_intelligence_generated', { organization_id: organizationId });
      // Invalidate business and task tracking queries so UI reflects generation in progress
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'business'] });
      queryClient.invalidateQueries({ queryKey: ['businessTaskTracking', businessId] });
    },
    onError: (error) => {
      console.error('Failed to generate brand intel:', error);
      toast({
        title: 'Brand Intel Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to trigger brand intel generation',
        variant: 'destructive',
      });
    },
  });
};

// On-demand AI generation of a single product (#5378). The backend appends the new product server-side,
// so success just invalidates the business query — the products list re-derives the new item on refetch.
export const useGenerateProductMutation = (organizationId: number, businessId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ['businesses', 'generate-product'],
    mutationFn: (context?: string) => generateProduct(businessId, context),
    onSuccess: () => {
      posthog?.capture('product_generated', { organization_id: organizationId });
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'business'] });
    },
    onError: (error) => {
      toast({
        title: 'Product Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate product',
        variant: 'destructive',
      });
    },
  });
};

// On-demand AI generation of a single customer persona / ICP (#5377). Mirror of the product mutation.
export const useGeneratePersonaMutation = (organizationId: number, businessId: number) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { toast } = useToast();

  return useMutation({
    mutationKey: ['businesses', 'generate-persona'],
    mutationFn: (context?: string) => generatePersona(businessId, context),
    onSuccess: () => {
      posthog?.capture('persona_generated', { organization_id: organizationId });
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'business'] });
    },
    onError: (error) => {
      toast({
        title: 'Customer Type Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate customer type',
        variant: 'destructive',
      });
    },
  });
};

export const useOrganizationDeleteMutationOptions = (organizationId: number) => {
  return useMutation({
    mutationKey: ['organizations', 'delete', organizationId],
    mutationFn: () => deleteOrganization(organizationId),
    onSuccess: (data) => {
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

// The currency alone, which owners AND admins may set (#6828); everything else on the organization stays on
// the owner-only update above.
export const useOrganizationCurrencyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['organizations', 'currency'],
    mutationFn: ({ organizationId, currency }: { organizationId: number; currency: string }) =>
      updateOrganizationCurrency(organizationId, currency),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      return data;
    },
  });
};

export const useOrganizationMutationOptions = () => {
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['organizations', 'update'],
    mutationFn: (changes: OrganizationChanges) => updateOrganization(changes),
    onSuccess: (data) => {
      posthog?.capture('organization_updated', { organization_id: data.id });
      // Refresh the organizations cache so consumers that re-derive org state from it (e.g. the
      // _authenticated AppShell re-seeding organizationDataAtom on remount) pick up the update.
      // Without this, the orgs query stays stale after an org update (e.g. the onboarding brand
      // wizard saving default_currency), leaving the "missing currency" attention badge on (#5610).
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      return data;
    },
    onError: (error, variables, context) => {
      console.error('error', error, variables, context);
    },
  });
};

export const useSendInvitationToUserMutation = () => {
  const posthog = usePostHog();

  return useMutation<
    InvitationResponse,
    Error,
    {
      email: string;
      role: string;
      organizationId: number;
    }
  >({
    mutationKey: ['users', 'invite'],
    mutationFn: ({ email, role, organizationId }) => sendInvitationToUser(email, role, organizationId),
    onSuccess: (_, variables) => {
      posthog?.capture('team_member_invited', { organization_id: variables.organizationId });
    },
  });
};

export const useResendInvitationMutation = () => {
  const posthog = usePostHog();

  return useMutation<
    { message: string },
    Error,
    {
      email: string;
      organizationId: number;
    }
  >({
    mutationKey: ['users', 'resend-invite'],
    mutationFn: ({ email, organizationId }) => resendOrganizationInvite(organizationId, email),
    onSuccess: (_, variables) => {
      posthog?.capture('team_invite_resent', { organization_id: variables.organizationId });
    },
  });
};

export const useCampaignTransferMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'transfer'],
    mutationFn: ({ campaignIds, targetOrganizationId, sourceOrganizationId }: {
      campaignIds: number[];
      targetOrganizationId: number;
      sourceOrganizationId: number;
    }) => transferCampaignsToOrganization({
      campaign_ids: campaignIds,
      target_organization_id: targetOrganizationId,
      source_organization_id: sourceOrganizationId,
    }),
    onSuccess: (_, variables) => {
      posthog?.capture('campaign_transferred', { campaign_count: variables.campaignIds.length, target_org_id: variables.targetOrganizationId });
      // Invalidate campaigns to refresh the list
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      // Invalidate organizations to update any counts
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

export const verifyInviteCodeQueryOptions = (code: string) => {
  return queryOptions({
    queryKey: ['verify-invite-code', code],
    queryFn: () => verifyInviteCode(code),
    retry: false, // Don't retry on 400 errors for invalid invite codes
    gcTime: 0, // Don't cache failed results
  });
};

export const campaignActionLogsQueryOptions = (slug: string) => {
  return queryOptions({
    queryKey: ['action-logs', slug],
    queryFn: () => fetchCampaignActionLogs(slug),
  });
};

export const useCampaignActionLogsQuery = (slug: string) => {
  return useQuery({
    ...campaignActionLogsQueryOptions(slug),
    enabled: !!slug
  });
};

export const useCampaignZeroImpressionOptimisationMutation = (slug: string) => {
  return useMutation({
    mutationKey: ['zero-impression-optimisation', slug],
    mutationFn: () => triggerZeroImpressionOptimisation(slug),
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
};

export const useCreateOnboardingCampaignMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'create-onboarding'],
    mutationFn: createOnboardingCampaign,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      // Campaign creation tracking
      posthog?.capture('onboarding_campaign_created', {
        campaign_id: data.id,
        website_url: data.website_url,
        campaign_status: data.state,
      });

      // Start Google Ads upload tracking
      if (data.is_celery_task) {
        posthog?.capture('onboarding_google_ads_upload_started', {
          campaign_id: data.id,
          campaign_type: 'onboarding',
          sync_mode: 'async',
        });
      }

      return data;
    },
    onError: (error) => {
      console.error('Onboarding campaign creation error:', error);
      posthog?.capture('onboarding_campaign_creation_failed', {
        error: error.message,
      });
    },
  });
};

export const useCreateBrandCampaignMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'create-brand'],
    mutationFn: createBrandCampaign,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      // Brand campaign creation tracking
      posthog?.capture('brand_campaign_created', {
        campaign_id: data.id,
        campaign_type: 'brand',
      });

      // Track Google Ads upload for brand campaigns
      if (data.is_celery_task) {
        posthog?.capture('onboarding_google_ads_upload_started', {
          campaign_id: data.id,
          campaign_type: 'brand',
          sync_mode: 'async',
        });
      }

      return data;
    },
    onError: (error) => {
      console.error('Brand campaign creation error:', error);
      posthog?.capture('brand_campaign_creation_failed', {
        error: error.message,
      });
    },
  });
};

export const useCreateCompetitorCampaignMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'create-competitor'],
    mutationFn: ({
      campaign,
      organizationId,
    }: {
      campaign: CompetitorCampaignCreate;
      organizationId: number;
    }) => createCompetitorCampaign(campaign, organizationId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['competitors'] });

      posthog?.capture('competitor_campaign_created', {
        campaign_id: data.campaign_id,
        campaign_type: 'competitor',
        competitors_count: data.competitors_count,
        high_intent_count: data.high_intent_count,
      });

      return data;
    },
    onError: (error) => {
      console.error('Competitor campaign creation error:', error);
      posthog?.capture('competitor_campaign_creation_failed', {
        error: error.message,
      });
    },
  });
};

// ─── Optimisation campaigns (HiPo / ICP / Location) ─────────────────────────
// Single endpoint, single mutation. Each call creates one Campaign with one Ad.
// For ICP fan-out (one Campaign per chosen ICP), call mutateAsync once per ICP
// from the wizard and aggregate the results. The mutation invalidates the
// /campaigns query on success so the new campaign appears in the sidebar.

export const useCreateOptimisationCampaignMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['campaigns', 'create-optimisation'],
    mutationFn: ({
      payload,
      organizationId,
      idempotencyKey,
    }: {
      payload: OptimisationCampaignCreatePayload;
      organizationId: number;
      idempotencyKey?: string;
    }): Promise<OptimisationCampaignCreateAccepted> =>
      createOptimisationCampaign(payload, organizationId, idempotencyKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      posthog?.capture('optimisation_campaign_created', {
        campaign_type: data.campaign_type,
        source_campaign_id: data.source_campaign_id,
      });
      return data;
    },
    onError: (error, variables) => {
      console.error('Optimisation campaign creation error:', error);
      posthog?.capture('optimisation_campaign_creation_failed', {
        campaign_type: variables.payload.campaign_type,
        error: error.message,
      });
    },
  });
};

// HiPo wizard step 2: list a source campaign's keywords with their (zero-
// defaulted) performance metrics so the user can multi-select which to
// inherit. Disabled until the user has picked a source on step 1.
export const campaignKeywordsWithPerfQueryOptions = (campaignId: number | null) => {
  return queryOptions<CampaignKeywordWithPerf[]>({
    queryKey: ['campaign-keywords-with-perf', campaignId],
    queryFn: () => getCampaignKeywordsWithPerf(campaignId as number),
    enabled: campaignId !== null && campaignId > 0,
    staleTime: 1000 * 60, // 1 minute — wizard step is short-lived
  });
};

// Conversion data is Google-Ads-account-scoped (and the account belongs to an
// org): both ids join the key and the org id is sent explicitly (#6450).
export const conversionTrackingQueryOptions = () => {
  return queryOptions({
    queryKey: ['conversion-tracking', activeOrgKey(), activeAccountKey()],
    queryFn: () => getConversionTrackingSettings(getCurrentOrganizationId()),
    enabled: orgSeeded(),
  });
};

export const conversionActionsQueryOptions = () => {
  return queryOptions({
    queryKey: ['conversion-actions', activeOrgKey(), activeAccountKey()],
    queryFn: () => listConversionActions(getCurrentOrganizationId()),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    enabled: orgSeeded(),
  });
};

// Conversion actions linked to one campaign. Campaign ids are globally unique and the
// backend 404s wrong-org campaigns, so the campaign-scoped key cannot leak across orgs;
// the key matches the tab's historical one so existing invalidations keep working.
// Hydrates the campaign draft's staged conversion edits (#6479) and feeds the tab's table.
export const campaignConversionActionsQueryOptions = (campaignId: number) =>
  queryOptions({
    queryKey: ['campaign', campaignId, 'conversion-actions'],
    queryFn: () => getCampaignConversionActions(campaignId),
    staleTime: 5 * 60 * 1000,
  });

export const conversionTrackingSnippetQueryOptions = (conversionActionId: string | null) => {
  return queryOptions({
    queryKey: ['conversion-tracking-snippet', activeOrgKey(), activeAccountKey(), conversionActionId],
    queryFn: () => getConversionActionTrackingSnippet(conversionActionId!, getCurrentOrganizationId()),
    enabled: !!conversionActionId && orgSeeded(),
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days — matches backend Redis TTL
    gcTime: 7 * 24 * 60 * 60 * 1000,
  });
};

export const useCreateWebsiteConversionMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['conversion-actions', 'create-website'],
    mutationFn: (request: WebsiteConversionCreateRequest) => createWebsiteConversion(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      toast({
        title: 'Success',
        description: 'Website conversion action created successfully',
      });
      posthog?.capture('website_conversion_created');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create website conversion',
        variant: 'destructive',
      });
    },
  });
};

export const useCreateCallConversionMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['conversion-actions', 'create-call'],
    mutationFn: (request: CallConversionCreateRequest) => createCallConversion(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      toast({
        title: 'Success',
        description: 'Call conversion action created successfully',
      });
      posthog?.capture('call_conversion_created');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create call conversion',
        variant: 'destructive',
      });
    },
  });
};

export const useRemoveConversionMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['conversion-actions', 'remove'],
    mutationFn: (conversionActionId: string) => removeConversionAction(conversionActionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      posthog?.capture('conversion_deleted');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove conversion action',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateConversionMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['conversion-actions', 'update'],
    mutationFn: ({ conversionActionId, request }: { conversionActionId: string; request: ConversionActionUpdateRequest }) =>
      updateConversionAction(conversionActionId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      toast({
        title: 'Success',
        description: 'Conversion action updated successfully',
      });
      posthog?.capture('conversion_updated');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update conversion action',
        variant: 'destructive',
      });
    },
  });
};

export const useActionLogsQuery = (
  campaignId: number,
  filters: ActionFilter = {},
  pagination: PaginationParams = { skip: 0, limit: 10 },
) => {
  return useQuery({
    queryKey: ['actionLogs', campaignId, filters, pagination],
    queryFn: () => fetchActionLogs(campaignId, filters, pagination),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    gcTime: 1000 * 60 * 10, // 10 minutes
    throwOnError: false,
  });
};

export const useAllActionLogsQuery = (
  filters: ActionFilter = {},
  pagination: PaginationParams = { skip: 0, limit: 100 },
) => {
  return useQuery({
    // The org id is an explicit key segment (#6489): `filters` alone hides it
    // from prefix invalidations, and hashing drops it while it's undefined.
    queryKey: ['actionLogs', 'all', filters.organization_id ?? null, filters, pagination],
    queryFn: () => fetchAllActionLogs(filters, pagination),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    retry: 1,
    throwOnError: false,
    // Without organization_id the endpoint aggregates action logs across EVERY
    // org the user belongs to (#6281) — fail closed until the caller has one.
    enabled: filters.organization_id != null,
  });
};

export const useCampaignTasksQuery = (campaignId: number, limit = 5) => {
  return useQuery({
    queryKey: ['campaignTasks', campaignId, limit],
    queryFn: () => fetchTasksForCampaign(campaignId, limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    gcTime: 1000 * 60 * 10, // 10 minutes
    throwOnError: false,
  });
};

export const useUndoActionMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: (actionId: number) => undoAction(actionId),
    onSuccess: () => {
      posthog?.capture('optimization_action_undone');
      // Invalidate action logs queries
      queryClient.invalidateQueries({ queryKey: ['actionLogs'] });
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Failed to undo action:', error);
    },
  });
};

export const useNegativeKeywordsReportQuery = (campaignSlug: string, days: number) => {
  return useQuery({
    queryKey: ['negative-keywords-report', campaignSlug, days],
    queryFn: () => fetchNegativeKeywordsReport(campaignSlug, days),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // 10 minutes cleanup
    enabled: !!campaignSlug,
  });
};

export const useDetectorKeywordsReportQuery = (campaignSlug: number, detector: string, threshold?: number) => {
  return useQuery({
    queryKey: ['admin', 'detector-keywords-report', campaignSlug, detector, threshold],
    queryFn: () => fetchDetectorKeywordsReport(campaignSlug, detector, threshold),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // 10 minutes cleanup
    enabled: !!campaignSlug,
  });
};

export const useNotificationQuery = (filters: NotificationFilter = {}, page: number = 1, page_size: number = 50) => {
  return useQuery({
    queryKey: [
      'notifications',
      filters.organization_id,
      filters.is_archived,
      filters.is_viewed,
      filters.sources?.sort().join(',') || '',
      filters.warning_levels?.sort().join(',') || '',
      filters.start_date || '',
      filters.end_date || '',
      filters.search_query || '',
      page,
      page_size
    ],
    queryFn: () => fetchNotificationItems(filters, page, page_size),
    // Keep previous data while fetching — but only within the same org (#6450):
    // an org switch must not paint the previous org's notifications.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[1] === filters.organization_id ? previousData : undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: !!filters.organization_id
  });
};

export const useReportsQuery = (organizationId: number | undefined, page: number = 1, pageSize: number = 50, reportType?: string) => {
  return useQuery({
    queryKey: ['reports', organizationId, page, pageSize, reportType || ''],
    queryFn: () => fetchOrganizationReports(organizationId!, page, pageSize, reportType),
    // Same-org only (#6450): never bridge an org switch with the old org's reports.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[1] === organizationId ? previousData : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });
};

export const useBiddingStrategyReportQuery = (dateRange: string = 'LAST_7_DAYS', campaignId?: number) => {
  return useQuery({
    queryKey: ['bidding-strategy-report', dateRange, campaignId, activeOrgKey(), activeAccountKey()],
    // The org id is read INSIDE the queryFn so it is captured at request time,
    // not frozen from a render that may predate an org switch (#6487).
    queryFn: () => fetchBiddingStrategyReport(dateRange, campaignId, undefined, getCurrentOrganizationId()),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: orgSeeded(),
  });
};

// #6501 — the PDF export fetches the same report imperatively (fetchQuery), so
// the options live here once and the hook + export share one key shape.
export const adPerformanceReportQueryOptions = (campaignIds?: string, startDate?: string, endDate?: string) =>
  queryOptions({
    queryKey: ['ad-performance-report', campaignIds, 'LAST_30_DAYS', startDate, endDate],
    queryFn: () => fetchAdPerformanceReport(campaignIds, 'LAST_30_DAYS', undefined, startDate, endDate),
    staleTime: PERF_STALE_TIME_MS,
    gcTime: PERF_GC_TIME_MS,
  });

export const useAdPerformanceReportQuery = (
  campaignIds?: string,
  dateRange: string = 'LAST_30_DAYS',
  options?: { startDate?: string; endDate?: string; enabled?: boolean },
) => {
  const { startDate, endDate, enabled } = options ?? {};
  return useQuery({
    // queryKey includes startDate/endDate so the previous-period fetch (#4293)
    // is cached independently from the current-period fetch.
    queryKey: ['ad-performance-report', campaignIds, dateRange, startDate, endDate, activeOrgKey(), activeAccountKey()],
    queryFn: () =>
      fetchAdPerformanceReport(campaignIds, dateRange, undefined, startDate, endDate, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled: (enabled ?? true) && orgSeeded(),
  });
};

// #4319 — perf-v2 chart time-series, current period. Cached per
// (campaigns, range, granularity) tuple; disabled until campaign ids resolve.
export const usePerformanceTimeSeriesQuery = (
  timeRange: 'daily' | 'weekly',
  dateRangeParam: string,
  campaignIds: string | undefined,
  // #6274 — explicit ISO window; when set, the backend ignores dateRangeParam
  // and the chart shares the exact same window as every report query.
  range?: { startDate: string; endDate: string },
  // #6276 — callers gate the fetch to tabs that actually render the series.
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['performance-timeseries', campaignIds, dateRangeParam, timeRange, range?.startDate, range?.endDate, activeOrgKey(), activeAccountKey()],
    queryFn: () =>
      fetchPerformanceReport(
        timeRange,
        dateRangeParam,
        campaignIds || 'all',
        undefined,
        range?.startDate,
        range?.endDate,
        getCurrentOrganizationId(),
      ),
    enabled: !!campaignIds && enabled && orgSeeded(),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
  });
};

// #4319 — perf-v2 chart time-series, previous period (comparison overlay).
// The explicit ISO start/end drive week / month / year-ago slices that the
// relative "30d"-style range string can't express; cached independently of
// the current-period fetch under its own queryKey.
export const usePreviousPerformanceTimeSeriesQuery = (
  timeRange: 'daily' | 'weekly',
  dateRangeParam: string,
  campaignIds: string | undefined,
  previousRange: { start_date?: string; end_date?: string },
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [
      'performance-timeseries-previous',
      campaignIds,
      timeRange,
      previousRange.start_date,
      previousRange.end_date,
      activeOrgKey(),
      activeAccountKey(),
    ],
    queryFn: () =>
      fetchPerformanceReport(
        timeRange,
        dateRangeParam,
        campaignIds || 'all',
        undefined,
        previousRange.start_date,
        previousRange.end_date,
        getCurrentOrganizationId(),
      ),
    enabled: !!campaignIds && enabled && orgSeeded(),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
  });
};

// Per-campaign status, IS, OptScore and bid strategy (#4300). Powers the
// "Status reasons / Search IS / Optimisation score / Bid strategy" columns on
// the perf-v2 Campaigns table. Lookup-by-campaign-id on the consumer side.
export const useCampaignStatusReportQuery = (
  campaignIds: string,
  startDate: string,
  endDate: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!campaignIds && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['campaign-status-report', campaignIds, startDate, endDate, activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchCampaignStatusReport(campaignIds, startDate, endDate, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// Account-wide keyword performance with Quality Score (#4295). Powers the
// perf-v2 Keywords tab. The backend issues two GAQL queries internally —
// segmented metrics + point-in-time QS — and merges by (ad_group_id,
// criterion_id), so the frontend sees a flat list of keyword rows.
export const useKeywordsOverviewReportQuery = (
  startDate: string,
  endDate: string,
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['keywords-overview-report', startDate, endDate, campaignIds ?? '', activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchKeywordsOverviewReport(startDate, endDate, campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// #5997 — per-ad-group landing-page experience buckets for the Landing Pages
// view. Point-in-time (NO_DATE), so no date args; long stale time + lazy.
export const useLandingPageExperienceQuery = (
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['landing-page-experience', campaignIds ?? '', activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchLandingPageExperience(campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled: (options?.enabled ?? true) && orgSeeded(),
  });
};

// Pending recommendations for the perf-v2 Overview Next-Steps widget (#4305).
// Auto-generated narrative insight cards for the Overview tab (#4304).
// Backend reads the campaign-status snapshot and asks the LLM to produce
// 3-5 cards (sentiment + category + narrative). Long staleTime because
// LLM responses are expensive and re-running on every focus change is
// wasteful — users can hard-refresh or change the date range to refetch.
export const usePerformanceInsightsQuery = (
  startDate: string,
  endDate: string,
  campaignIds: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && !!campaignIds && orgSeeded();
  return useQuery({
    queryKey: ['performance-insights', startDate, endDate, campaignIds, activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchPerformanceInsights(startDate, endDate, campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// Per-campaign × conversion-action breakdown (#4301). Powers two surfaces:
// the Campaigns 'By Conversion Action' grouping (table) + the Overview
// 'Conversion mix' donut. Lazy — only fetched when one of those surfaces
// is visible.
export const useAccountConversionActionsReportQuery = (
  startDate: string,
  endDate: string,
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['conversion-actions-report', startDate, endDate, campaignIds ?? '', activeOrgKey(), activeAccountKey()],
    queryFn: () =>
      fetchAccountConversionActionsReport(startDate, endDate, campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// Account-wide demographics report (#4298). Powers the Demographics sub-view
// of the perf-v2 Audience tab. dimension switches which Google Ads view the
// backend queries (age_range_view / gender_view / parental_status_view) so
// changing the dropdown is a cheap re-fetch, not a client-side reshape.
export const useAccountDemographicsReportQuery = (
  dimension: 'age' | 'gender' | 'parental_status',
  startDate: string,
  endDate: string,
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['demographics-report', dimension, startDate, endDate, campaignIds ?? '', activeOrgKey(), activeAccountKey()],
    queryFn: () =>
      fetchAccountDemographicsReport(dimension, startDate, endDate, campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// #5922 — account-wide geography (per country) for the Audience > Geography
// sub-view. Lazy on the sub-view so visiting Audience alone doesn't fire it.
export const useAccountGeographyReportQuery = (
  startDate: string,
  endDate: string,
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['geography-report', startDate, endDate, campaignIds ?? '', activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchAccountGeographyReport(startDate, endDate, campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// #5922 — account-wide device (per device) for the Audience > Device sub-view.
export const useAccountDeviceReportQuery = (
  startDate: string,
  endDate: string,
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['device-report', startDate, endDate, campaignIds ?? '', activeOrgKey(), activeAccountKey()],
    queryFn: () => fetchAccountDeviceReport(startDate, endDate, campaignIds, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// Merged Activity feed (#4297): Google Ads change_event + Glitch ActionLog,
// unified by the backend and sorted newest-first. Powers the perf-v2 Activity
// tab. Lazy — only fires when the tab is active, since change_event can be
// quota-heavy for busy accounts.
export const useActivityFeedQuery = (
  startDate: string,
  endDate: string,
  organizationId: number | undefined,
  campaignIds?: string,
  options?: { enabled?: boolean },
) => {
  // #6487 — campaignIds is REQUIRED: an unset campaign_ids param means
  // ACCOUNT-WIDE to the backend, silently replacing "the filter matched no
  // campaigns" with an unscoped feed (the same trap as #6275). Callers pass
  // the scoped id list or the query stays off.
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && !!organizationId && !!campaignIds;
  return useQuery({
    // The Google half of the feed is account-scoped, so the selected account
    // joins the key (#6489) — an account switch must never serve the previous
    // account's change history. Invalidated centrally on account selection.
    queryKey: ['activity-feed', startDate, endDate, organizationId, campaignIds ?? '', activeAccountKey()],
    queryFn: () => fetchActivityFeed(startDate, endDate, organizationId as number, campaignIds),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// Account-wide search-terms report (#4296). Powers the perf-v2 Search Terms
// tab. Returns one row per (search_term, ad_group, matched keyword) with
// search_term_view.status (ADDED / EXCLUDED / NONE) so the table can colour-
// code which terms are already on a list. Lazy — only fetches when the tab
// is active, since the search-terms query can be expensive on busy accounts.
export const useAccountSearchTermsReportQuery = (
  startDate: string,
  endDate: string,
  campaignIds?: string,
  minImpressions: number = 1,
  options?: { enabled?: boolean },
) => {
  const enabled = (options?.enabled ?? true) && !!startDate && !!endDate && orgSeeded();
  return useQuery({
    queryKey: ['account-search-terms-report', startDate, endDate, campaignIds ?? '', minImpressions, activeOrgKey(), activeAccountKey()],
    queryFn: () =>
      fetchAccountSearchTermsReport(startDate, endDate, campaignIds, minImpressions, undefined, getCurrentOrganizationId()),
    staleTime: PERF_STALE_TIME_MS, // #6293 — serve tab switches from cache for an hour
    // #6497 — filter/window changes repaint over the previous rows instead of
    // blanking the panel while the refetch runs.
    placeholderData: keepPreviousData,
    gcTime: PERF_GC_TIME_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
};

// Search Terms Report (RMF R.70 compliance)
export const useSearchTermsReportQuery = (days: number = 30, organizationId?: number) => {
  return useQuery({
    queryKey: ['search-terms-report', days, organizationId],
    queryFn: () => fetchSearchTermsReport(days, organizationId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
    // Same-org only (#6450).
    placeholderData: (prev, previousQuery) =>
      previousQuery?.queryKey?.[2] === organizationId ? prev : undefined,
  });
};

export const useUpdateNotificationItemMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Pick<NotificationItem, 'is_viewed' | 'is_archived'>>;
    }) => updateNotificationItem(id, updates),
    onMutate: async ({ id, updates }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      // Snapshot every notifications cache so onError can roll back (#5082)
      const previousQueries = queryClient.getQueriesData<NotificationResponse>({ queryKey: ['notifications'] });

      // Optimistically update all notification queries
      queryClient.setQueriesData<NotificationResponse>(
        { queryKey: ['notifications'] },
        (oldData) => {
          // Only touch caches that actually contain the item — other
          // filters/pages must keep their items and totals (#5082)
          if (!oldData?.items?.some((item: NotificationItem) => item.id === id)) return oldData;

          // If archiving/unarchiving, remove from current view
          if (updates.is_archived !== undefined) {
            // Remove the item from this query's results since it no longer matches the filter
            const updatedItems = oldData.items.filter((item: NotificationItem) => item.id !== id);
            return {
              ...oldData,
              items: updatedItems,
              total: Math.max(0, (oldData.total || 0) - 1),
            };
          }

          // For other updates (is_viewed), update in place
          return {
            ...oldData,
            items: oldData.items.map((item: NotificationItem) =>
              item.id === id ? { ...item, ...updates } : item
            )
          };
        }
      );

      return { previousQueries };
    },
    onError: (_error, _variables, context) => {
      // Roll back the optimistic writes, then refetch fresh data
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onSuccess: (_, variables) => {
      // Invalidate to refetch with accurate counts and data
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationCounts'],
        exact: false,
      });
      posthog?.capture('notification_updated', { notification_id: variables.id, ...variables.updates });
    },
  });
};

export const useBulkUpdateNotificationsMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: ({
      notificationIds,
      action,
    }: {
      notificationIds: string[];
      action: import('./types').BulkNotificationAction;
    }) => bulkUpdateNotifications(notificationIds, action),
    onMutate: async ({ notificationIds, action }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      // Determine what updates to apply
      const updates: Partial<NotificationItem> = {};
      switch (action) {
        case 'mark_read':
          updates.is_viewed = true;
          break;
        case 'mark_unread':
          updates.is_viewed = false;
          break;
        case 'archive':
          updates.is_archived = true;
          break;
        case 'unarchive':
          updates.is_archived = false;
          break;
      }

      // Optimistically update UI state only (not counts)
      queryClient.setQueriesData(
        { queryKey: ['notifications'] },
        (oldData: any) => {
          if (!oldData?.items) return oldData;

          // Update the items in the list to show visual feedback
          // Don't remove items or modify count - let refetch handle that accurately
          return {
            ...oldData,
            items: oldData.items.map((item: NotificationItem) =>
              notificationIds.includes(item.id) ? { ...item, ...updates } : item
            )
          };
        }
      );
    },
    onError: () => {
      // On error, invalidate to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
    },
    onSuccess: (_, variables) => {
      // Invalidate to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationCounts'],
        exact: false,
      });
      posthog?.capture('notifications_bulk_updated', { count: variables.notificationIds.length, action: variables.action });
    },
  });
};


export const useNotificationCountsQuery = (
  organizationId: number,
  filters?: Partial<NotificationFilter>
) => {
  return useQuery({
    queryKey: ['notificationCounts', organizationId, filters],
    queryFn: () => fetchNotificationCounts(organizationId, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId
  });
};

export const useInvoicesQuery = (organizationId: number, options?: Omit<Parameters<typeof useQuery<InvoiceHistoryResponse>>[0], 'queryKey' | 'queryFn'>) => {

  return useQuery<InvoiceHistoryResponse>({
    queryKey: ['invoices', organizationId],
    queryFn: () => fetchInvoices(organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};


// HubSpot Integration Query Options
export const hubspotIntegrationStatusQueryOptions = (enabled: boolean = true) => {
  return queryOptions({
    queryKey: ['hubspot', 'integration', 'status', activeOrgKey()],
    queryFn: getHubSpotIntegrationStatus,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    // Callers combine INTO this via the parameter — a spread-site
    // `enabled:` override would silently drop the org-seeded gate (#6489).
    enabled: enabled && orgSeeded(),
  });
};

export const useHubSpotIntegrationStatus = (options?: { enabled?: boolean }) => {
  return useQuery(hubspotIntegrationStatusQueryOptions(options?.enabled ?? true));
};

// Meta Ads Integration Query Options
export const metaIntegrationStatusQueryOptions = (enabled: boolean = true) => {
  return queryOptions({
    queryKey: ['meta', 'integration', 'status', activeOrgKey()],
    // Captured at request time (#6487): the active org travels as an explicit
    // organization_id param instead of the backend's last_organization_id.
    queryFn: () => getMetaIntegrationStatus(getCurrentOrganizationId()),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    // Callers combine INTO this via the parameter — a spread-site
    // `enabled:` override would silently drop the org-seeded gate (#6489).
    enabled: enabled && orgSeeded(),
  });
};

export const useMetaIntegrationStatus = (options?: { enabled?: boolean }) => {
  return useQuery(metaIntegrationStatusQueryOptions(options?.enabled ?? true));
};


export const useSelectMetaAdAccountMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: (metaAdAccountId: number) => selectMetaAdAccount(metaAdAccountId),
    onSuccess: (_data, metaAdAccountId) => {
      posthog?.capture('meta_ad_account_selected', { meta_ad_account_id: metaAdAccountId });
      queryClient.invalidateQueries({ queryKey: ['meta', 'ad-accounts'] });
      // Account-scoped caches keep serving the PREVIOUS account for their 60s–5min staleTimes after
      // a switch — invalidate them so the new account's data loads immediately (#6163 P0-4).
      queryClient.invalidateQueries({ queryKey: ['meta', 'dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'conversions'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'conversion-events'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'custom-audiences'] });
      // Returned (not fired-and-forgotten) so mutateAsync resolves only once the status query
      // reflects the switch — otherwise the account modal's CONTINUE re-enables while
      // selected_ad_account still holds the OLD account and step 2 renders its Page/Pixel (#6179).
      return queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
    },
  });
};

/**
 * Pages the user admins for a Meta ad account (#4277 chunk 3).
 *
 * Backed by Graph /me/accounts — the same list applies across all ad accounts the user owns
 * (it's keyed by the user's token, not the ad account), so the cache key includes the
 * ad-account id only to scope ownership checks. Cached for 5 min so opening/closing the picker
 * doesn't refetch unnecessarily.
 */
export const metaPagesQueryOptions = (metaAdAccountId: number | null | undefined) =>
  queryOptions({
    queryKey: ['meta', 'ad-accounts', metaAdAccountId, 'pages'],
    queryFn: () => listMetaPagesForAdAccount(metaAdAccountId as number),
    enabled: !!metaAdAccountId,
    staleTime: 5 * 60 * 1000,
  });

export const useSelectMetaPageMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: (data: MetaPageSelectRequest) => selectMetaPageForAdAccount(data),
    onSuccess: (_data, variables) => {
      posthog?.capture('meta_page_selected', {
        meta_ad_account_id: variables.meta_ad_account_id,
        page_id: variables.page_id,
      });
      // Both the integration status (which exposes selected_ad_account.page_*) and the ad-accounts
      // list re-render off this; invalidate both so the Settings card + wizard pick up the change.
      queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'ad-accounts'] });
    },
  });
};

/**
 * Pixels on a Meta ad account (Phase 7 Chunk 0).
 *
 * Pixels are ad-account-scoped (Graph /act_{id}/adspixels), so the cache key is keyed by the
 * ad-account id. Cached for 5 min so opening/closing the picker doesn't refetch unnecessarily.
 */
export const metaPixelsQueryOptions = (metaAdAccountId: number | null | undefined) =>
  queryOptions({
    queryKey: ['meta', 'ad-accounts', metaAdAccountId, 'pixels'],
    queryFn: () => listMetaPixelsForAdAccount(metaAdAccountId as number),
    enabled: !!metaAdAccountId,
    staleTime: 5 * 60 * 1000,
  });

/** The bound Page's PUBLISHED instant forms (#6186) — the wizard's form picker for INSTANT_FORM_LEADS. */
export const metaLeadgenFormsQueryOptions = (metaAdAccountId: number | null | undefined) =>
  queryOptions({
    queryKey: ['meta', 'ad-accounts', metaAdAccountId, 'leadgen-forms'],
    queryFn: () => listMetaLeadgenFormsForAdAccount(metaAdAccountId as number),
    enabled: !!metaAdAccountId,
    staleTime: 5 * 60 * 1000,
  });

export const useSelectMetaPixelMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: (data: MetaPixelSelectRequest) => selectMetaPixelForAdAccount(data),
    onSuccess: (_data, variables) => {
      posthog?.capture('meta_pixel_selected', {
        meta_ad_account_id: variables.meta_ad_account_id,
        pixel_id: variables.pixel_id,
      });
      // The integration status surfaces selected_ad_account.pixel_*; the ad-accounts list re-renders
      // off this too. Invalidate both so the Settings card + wizard pick up the change.
      queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'ad-accounts'] });
      // Binding a Pixel changes conversion tracking, so the Conversions tab is stale too (#6163 P1-6).
      queryClient.invalidateQueries({ queryKey: ['meta', 'conversions'] });
    },
  });
};

/**
 * #4288 Phase 1a/1c Meta CBO campaign-budget mutation. Hits the unified
 * ``PATCH /budgets/meta/campaigns/{id}`` endpoint. Invalidates the broad Meta campaign cache so
 * any list / detail view re-renders with the new value.
 */
export const useUpdateMetaCampaignBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, daily_budget_cents }: { campaignId: number; daily_budget_cents: number; skipInvalidate?: boolean }) =>
      updateMetaCampaignBudget(campaignId, { daily_budget_cents }),
    onSuccess: (_data, variables) => {
      // The batched budget save passes skipInvalidate and invalidates once for the whole batch, not
      // 3× per row (which storm-refetched the dashboards/placement every row) — #6163 P1-15.
      if (variables.skipInvalidate) return;
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
      // Prefix-match the slug-keyed detail (['meta','campaign', slug]) — the budget endpoint is
      // still id-addressed, but the detail cache moved to slugs in #4659, so invalidate by prefix
      // (matches the sibling ad-set budget mutation below).
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign'] });
      // Budget-pacing is the only dashboard that renders budgets; the others show insight
      // snapshots a budget PATCH cannot change. Scoped so a budget save never refetches
      // 'placement', which is a live Graph call per observer (#6163 P3, #6179).
      queryClient.invalidateQueries({ queryKey: ['meta', 'dashboards', 'budget-pacing'] });
    },
  });
};

/**
 * #4288 Phase 1a/1c Meta ABO ad-set-budget mutation. Hits the unified
 * ``PATCH /budgets/meta/ad-sets/{id}`` endpoint. Same invalidations as the campaign mutation
 * (the campaign-detail query carries the ad-set tree, so the list cache and detail cache both
 * need to re-render).
 */
export const useUpdateMetaAdSetBudgetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adSetId, daily_budget_cents }: { adSetId: number; daily_budget_cents: number; skipInvalidate?: boolean }) =>
      updateMetaAdSetBudget(adSetId, { daily_budget_cents }),
    onSuccess: (_data, variables) => {
      if (variables.skipInvalidate) return; // batched save invalidates once (#6163 P1-15)
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign'] });
      // Scoped to budget-pacing — see the campaign-budget mutation above (#6179).
      queryClient.invalidateQueries({ queryKey: ['meta', 'dashboards', 'budget-pacing'] });
    },
  });
};

/**
 * #4288 Phase 1c launch / pause mutation — flips a Meta campaign's status (and its ad sets + ads,
 * via the backend cascade) between ``ACTIVE`` and ``PAUSED``. Phase 3's approval-flow 'Go Live'
 * button is the primary caller. Invalidates the campaign list + detail caches so the new status
 * surfaces wherever it's rendered.
 */
export const useUpdateMetaCampaignStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, status }: { slug: string; status: MetaEntityStatus }) =>
      updateMetaCampaignStatus(slug, { status }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', variables.slug] });
      // Status changes (launch/pause) flip the entity's live/paused state on the dashboards too.
      queryClient.invalidateQueries({ queryKey: ['meta', 'dashboards'] });
    },
  });
};

export const useDeleteMetaCampaignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => deleteMetaCampaign(slug),
    onSuccess: (_data, slug) => {
      // Remove (not invalidate) the deleted campaign's detail tree — the caller navigates away, and
      // an invalidate would refetch a campaign the UI no longer shows (#6179).
      queryClient.removeQueries({ queryKey: ['meta', 'campaign', slug] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
      // The deleted campaign's budget no longer counts against the Meta allocation headroom.
      queryClient.invalidateQueries({ queryKey: ['meta', 'dashboards', 'budget-pacing'] });
    },
  });
};

// Meta Ads — campaigns. Org-scoped (#6450): the org id joins the key (index 2)
// and is sent explicitly with the request.
export const metaCampaignsQueryOptions = (offerUrl?: string, orgId?: number | null) => {
  const organizationId = orgId ?? getCurrentOrganizationId() ?? null;
  return queryOptions({
    queryKey: ['meta', 'campaigns', organizationId, offerUrl ?? 'all'],
    queryFn: () => listMetaCampaigns(offerUrl, organizationId ?? getCurrentOrganizationId()),
    staleTime: 30 * 1000,
    // No fetch into the null-org slot before the org context exists (#6489).
    enabled: organizationId !== null,
  });
};

export const metaCampaignDetailQueryOptions = (slug: string) => {
  return queryOptions({
    queryKey: ['meta', 'campaign', slug],
    queryFn: () => getMetaCampaign(slug),
    enabled: !!slug,
  });
};

/**
 * On-demand budget recommendation for the Budget tab's Get Recommendation button (#5981).
 * Fetched imperatively via `queryClient.fetchQuery` on click — never observed by a component, and
 * the default staleTime keeps every click a fresh computation (the figure derives from trailing
 * CPL, which moves) (#6182).
 */
export const metaBudgetRecommendationQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ['meta', 'campaign', slug, 'budget-recommendation'],
    queryFn: () => getMetaBudgetRecommendation(slug),
  });

// Phase 7 (#3824) snapshot-backed dashboards. 4-hour refresh server-side, so a 5-minute staleTime
// keeps the UI responsive without hammering the endpoints.
// #6293 — the perf surface serves tab switches from cache for an hour.
const META_DASHBOARD_STALE_TIME = PERF_STALE_TIME_MS;

// #6487/#6489 — every dashboard queryFn captures the active org AT REQUEST
// TIME (explicit organization_id param), and every query waits for the org
// mirror to seed before its first fetch.
export const metaCampaignOverviewQueryOptions = (enabled = true) => {
  return queryOptions({
    queryKey: ['meta', 'dashboards', 'campaign-overview', activeOrgKey()],
    queryFn: () => getMetaCampaignOverview(getCurrentOrganizationId()),
    staleTime: META_DASHBOARD_STALE_TIME,
    gcTime: PERF_GC_TIME_MS,
    enabled: enabled && orgSeeded(),
  });
};

export const metaTimeSeriesQueryOptions = (startDate: string, endDate: string, enabled = true) => {
  return queryOptions({
    queryKey: ['meta', 'dashboards', 'time-series', startDate, endDate, activeOrgKey()],
    queryFn: () => getMetaTimeSeries(startDate, endDate, undefined, getCurrentOrganizationId()),
    staleTime: META_DASHBOARD_STALE_TIME,
    gcTime: PERF_GC_TIME_MS,
    enabled: enabled && !!startDate && !!endDate && orgSeeded(),
  });
};

export const metaCreativePerformanceQueryOptions = (enabled = true) => {
  return queryOptions({
    queryKey: ['meta', 'dashboards', 'creative-performance', activeOrgKey()],
    queryFn: () => getMetaCreativePerformance(getCurrentOrganizationId()),
    staleTime: META_DASHBOARD_STALE_TIME,
    gcTime: PERF_GC_TIME_MS,
    enabled: enabled && orgSeeded(),
  });
};

export const metaAdSetPerformanceQueryOptions = (enabled = true) => {
  return queryOptions({
    queryKey: ['meta', 'dashboards', 'adset-performance', activeOrgKey()],
    queryFn: () => getMetaAdSetPerformance(getCurrentOrganizationId()),
    staleTime: META_DASHBOARD_STALE_TIME,
    gcTime: PERF_GC_TIME_MS,
    enabled: enabled && orgSeeded(),
  });
};

export const metaBudgetPacingQueryOptions = (enabled = true) => {
  return queryOptions({
    queryKey: ['meta', 'dashboards', 'budget-pacing', activeOrgKey()],
    queryFn: () => getMetaBudgetPacing(getCurrentOrganizationId()),
    staleTime: META_DASHBOARD_STALE_TIME,
    gcTime: PERF_GC_TIME_MS,
    enabled: enabled && orgSeeded(),
  });
};

// Per-placement (publisher_platform) metrics — a live account-level breakdown (#6049).
export const metaPlacementQueryOptions = (enabled = true, startDate?: string, endDate?: string) => {
  return queryOptions({
    queryKey: ['meta', 'dashboards', 'placement', startDate, endDate, activeOrgKey()],
    queryFn: () => getMetaPlacement(startDate, endDate, getCurrentOrganizationId()),
    staleTime: META_DASHBOARD_STALE_TIME,
    gcTime: PERF_GC_TIME_MS,
    enabled: enabled && orgSeeded(),
  });
};

/**
 * Per-resource progress for an in-flight Meta campaign generation (#4288 Phase 4). Polls every 3
 * seconds while the underlying TaskTracking row is `IN_PROGRESS`; stops once it flips to
 * `COMPLETED` / `FAILED`. The detail panel uses this to render the "Generating Campaign" card.
 */
export const metaCampaignProgressQueryOptions = (slug: string) => {
  return queryOptions<MetaCampaignGenerationProgress, Error>({
    queryKey: ['meta', 'campaign-progress', slug],
    queryFn: () => getMetaCampaignProgress(slug),
    enabled: !!slug,
    refetchInterval: (query) => (query.state.data?.state === 'IN_PROGRESS' ? 3000 : false),
  });
};

export const metaCampaignTaskStatusQueryOptions = (offerUrl: string | null) => {
  return queryOptions<MetaCampaignTaskStatusResponse | null, Error>({
    queryKey: ['meta', 'campaign-task-status', offerUrl ?? '', activeOrgKey()],
    queryFn: () => (offerUrl ? getMetaCampaignTaskStatus(offerUrl) : Promise.resolve(null)),
    enabled: !!offerUrl && orgSeeded(),
    // Poll while the background creation is running, then stop.
    refetchInterval: (query) => (query.state.data?.state === 'IN_PROGRESS' ? 3000 : false),
  });
};

export const useCreateMetaPhaseCampaignMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: (data: MetaPhaseCampaignRequest) => createMetaPhaseCampaign(data),
    onSuccess: (_data, variables) => {
      posthog?.capture('meta_phase_campaign_dispatched', {
        offer_url: variables.offer_url,
        phase: variables.phase,
      });
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign-task-status', variables.offer_url] });
      // The dispatch creates the campaign row synchronously, so the list cache is stale the moment
      // this resolves. Owned here, not by callers (#6163 P2, #6179): mutations invalidate what
      // they change.
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaigns'] });
    },
  });
};

/**
 * Generation ACTIVITY-feed events for a Meta campaign (#5981). Polls every 3 seconds while the
 * campaign is generating (same cadence as the progress card); once generation ends the last
 * snapshot stays cached so the completed feed remains visible, like Google's.
 */
export const metaCampaignEventsQueryOptions = (slug: string, isGenerating: boolean) => {
  return queryOptions({
    queryKey: ['meta', 'campaign-events', slug],
    queryFn: () => fetchMetaCampaignEventsHistory(slug),
    enabled: !!slug,
    refetchInterval: isGenerating ? 3000 : false,
  });
};

/** The Conversions tab's data (#5981): linked HubSpot→Meta mappings + linkable stages. */
export const metaConversionsQueryOptions = () => {
  return queryOptions({
    queryKey: ['meta', 'conversions', activeOrgKey()],
    queryFn: () => getMetaConversions(),
    staleTime: 60 * 1000,
    enabled: orgSeeded(),
  });
};

/** Link / unlink a HubSpot stage mapping's Meta uploads (#5981). Refreshes the Conversions tab. */
export const useLinkMetaConversionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageMappingId: number) => linkMetaConversion(stageMappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta', 'conversions'] });
    },
  });
};

export const useUnlinkMetaConversionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageMappingId: number) => unlinkMetaConversion(stageMappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meta', 'conversions'] });
    },
  });
};

/**
 * Update a Meta campaign's bidding (#5981) — the Budget tab's Bidding panel save. The response is
 * the refreshed detail tree; seed it into the slug-keyed cache so the panel re-renders without a
 * refetch.
 */
export const useUpdateMetaCampaignBiddingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: MetaCampaignBiddingUpdateRequest }) =>
      updateMetaCampaignBidding(slug, body),
    onSuccess: (data, variables) => {
      // The PATCH returns the refreshed detail tree (response_model=MetaCampaignDetailResponse),
      // so seeding the cache IS the refresh — no same-key invalidate, which refetched the data we
      // were just handed (#6163 P3, #6179).
      queryClient.setQueryData(['meta', 'campaign', variables.slug], data);
    },
  });
};

/**
 * Live interest search for the Targeting tab's Interests picker (#6076). Keyed by query so typing
 * back a previous term is instant; disabled until the user types.
 */
export const metaInterestSearchQueryOptions = (query: string) =>
  queryOptions({
    queryKey: ['meta', 'interests', activeOrgKey(), query],
    queryFn: () => searchMetaInterests(query),
    enabled: query.trim().length > 0 && orgSeeded(),
    staleTime: 60 * 60 * 1000,
  });

/** The optimization-event catalog (#6076) — the Conversions tab's picker options. */
export const metaConversionEventsQueryOptions = (outcome?: MetaCampaignOutcome) =>
  queryOptions({
    queryKey: ['meta', 'conversion-events', outcome ?? 'all', activeOrgKey()],
    queryFn: () => getMetaConversionEvents(outcome),
    staleTime: 5 * 60 * 1000,
    enabled: orgSeeded(),
  });

/**
 * Update a Meta campaign's conversion settings (#6076) — the Conversions tab's Optimization save.
 * The response is the refreshed detail tree; seed the slug-keyed cache so the section re-renders.
 */
export const useUpdateMetaConversionSettingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: MetaConversionSettingsUpdateRequest }) =>
      updateMetaConversionSettings(slug, body),
    onSuccess: (data, variables) => {
      // The PATCH returns the refreshed detail tree (response_model=MetaCampaignDetailResponse),
      // so seeding the cache IS the refresh — no same-key invalidate, which refetched the data we
      // were just handed (#6163 P3, #6179).
      queryClient.setQueryData(['meta', 'campaign', variables.slug], data);
    },
  });
};

/** The account's saved custom audiences (#6076) — the Audience sub-tab's checkbox catalog. */
export const metaCustomAudiencesQueryOptions = () =>
  queryOptions({
    queryKey: ['meta', 'custom-audiences', activeOrgKey()],
    queryFn: () => getMetaCustomAudiences(),
    staleTime: 5 * 60 * 1000,
    enabled: orgSeeded(),
  });

/**
 * Live locale search for the Targeting tab's Languages picker (#5981). Keyed by query so typing
 * back a previous term is instant; disabled until the user types.
 */
export const metaLocaleSearchQueryOptions = (query: string) =>
  queryOptions({
    queryKey: ['meta', 'locales', query],
    queryFn: () => searchMetaLocales(query),
    enabled: query.trim().length > 0,
    staleTime: 60 * 60 * 1000,
  });

/**
 * Replace a Meta campaign's geo targeting (#5981) — the Targeting tab's save. The response is the
 * refreshed detail tree; seed it into the slug-keyed cache so the tab re-renders without a refetch.
 */
/** One ad's Meta-rendered preview (#6213). Cached per (ad, format) — previews are stable snapshots. */
export const metaAdPreviewQueryOptions = (adId: number, adFormat: string) =>
  queryOptions({
    queryKey: ['meta', 'ad-preview', adId, adFormat],
    queryFn: () => getMetaAdPreview(adId, adFormat),
    staleTime: 5 * 60 * 1000,
  });

/** Add ONE new ad set to a pushed Meta campaign (#6211) — 202; the modal polls the task status. */
export const useCreateMetaAdSetMutation = () => {
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: MetaAdSetCreateRequest }) => createMetaAdSet(slug, body),
  });
};

/**
 * Status of the most recent add-ad-set task (#6211). Polls while IN_PROGRESS; the add-ad-set
 * modal owns the terminal handling (invalidate the campaign tree on COMPLETED, show FAILED inline).
 */
export const metaAdSetTaskStatusQueryOptions = (slug: string, enabled: boolean) => {
  return queryOptions<MetaCampaignTaskStatusResponse | null, Error>({
    queryKey: ['meta', 'ad-set-task-status', slug],
    queryFn: () => getMetaAdSetTaskStatus(slug),
    enabled,
    refetchInterval: (query) => (query.state.data?.state === 'IN_PROGRESS' || query.state.data == null ? 2000 : false),
  });
};

/** Switch a Meta campaign between ABO and CBO (#6211). The PATCH returns the refreshed tree. */
export const useUpdateMetaCampaignBudgetModeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: MetaBudgetModeUpdateRequest }) =>
      updateMetaCampaignBudgetMode(slug, body),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['meta', 'campaign', variables.slug], data);
    },
  });
};

export const useUpdateMetaCampaignTargetingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }: { slug: string; body: MetaCampaignTargetingUpdateRequest }) =>
      updateMetaCampaignTargeting(slug, body),
    onSuccess: (data, variables) => {
      // The PATCH returns the refreshed detail tree (response_model=MetaCampaignDetailResponse),
      // so seeding the cache IS the refresh — no same-key invalidate, which refetched the data we
      // were just handed (#6163 P3, #6179).
      queryClient.setQueryData(['meta', 'campaign', variables.slug], data);
    },
  });
};

/** Rename ONE ad set (#6210) — the ad-set surface's editable name. Refreshes the campaign tree. */
export const useUpdateMetaAdSetNameMutation = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adSetId, name }: { adSetId: number; name: string }) => updateMetaAdSetName(adSetId, name),
    onSuccess: () => {
      // The detail tree carries the ad set's name — one invalidation refreshes the ad-set surface,
      // the sidebar tier, and the campaign's Ad Sets table together.
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', campaignSlug] });
    },
  });
};

export const useUpdateMetaAdStatusMutation = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adId, status }: { adId: number; status: MetaEntityStatus }) => updateMetaAdStatus(adId, { status }),
    onSuccess: () => {
      // The detail tree carries every ad's status — one invalidation refreshes the ad page,
      // the Ad Sets table, and the Assets tab together (#6179).
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', campaignSlug] });
    },
  });
};

export const useDeleteMetaAdMutation = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (adId: number) => deleteMetaAd(adId),
    onSuccess: () => {
      // The archived ad drops out of the refetched detail tree (the backend filters ARCHIVED
      // children), which empties the ad page — its caller navigates back to the campaign (#6179).
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', campaignSlug] });
    },
  });
};

/**
 * Inline copy editing for one Meta ad from the campaign screen (#5981). Meta creatives are
 * immutable, so a save rebuilds the creative server-side and swaps it onto the ad; the campaign
 * detail query is invalidated so the panel re-renders the persisted copy.
 */
export const useUpdateMetaAdCopyMutation = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: ({ adId, body }: { adId: number; body: MetaAdCopyUpdateRequest }) => updateMetaAdCopy(adId, body),
    onSuccess: (_data, variables) => {
      posthog?.capture('meta_ad_copy_updated', { ad_id: variables.adId });
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', campaignSlug] });
    },
  });
};

/**
 * One-click copy regeneration for one Meta ad (#5981) — the backend runs a fresh LLM pass against
 * the campaign's offer URL and applies it through the same rebuild-and-swap path as the manual edit.
 */
export const useRegenerateMetaAdCopyMutation = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: (adId: number) => regenerateMetaAdCopy(adId),
    onSuccess: (_data, adId) => {
      posthog?.capture('meta_ad_copy_regenerated', { ad_id: adId });
      queryClient.invalidateQueries({ queryKey: ['meta', 'campaign', campaignSlug] });
    },
  });
};

export const hubspotPipelinesQueryOptions = (organizationId: number) => {
  return queryOptions({
    queryKey: ['hubspot', 'pipelines', organizationId],
    queryFn: () => getHubSpotPipelines(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSaveHubSpotPipelineConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, data }: { organizationId: number; data: Parameters<typeof saveHubSpotPipelineConfig>[1] }) =>
      saveHubSpotPipelineConfig(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubspot'] });
    },
  });
};

export const useSetupHubSpotConversions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, configId }: { organizationId: number; configId: number }) =>
      setupHubSpotConversions(organizationId, configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubspot'] });
      // Setup registers/updates conversion actions in the registry — refresh the lists that show them.
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
  });
};

export const googleAdsAccountUsersQueryOptions = () => {
  return queryOptions({
    queryKey: ['googleAdsAccountUsers'],
    queryFn: () => fetchGoogleAdsAccountUsers(),
    // A 404 means no account is selected for the organization (#6827): retrying only delays the prompt.
    retry: (failureCount, error) =>
      (error as Error & { status?: number }).status !== 404 && failureCount < 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

import { getLocationRecommendations } from './apis';

// Add location recommendations query
export const locationRecommendationsQueryOptions = (organizationId: string) => {
  return queryOptions({
    queryKey: ['recommendations', 'locations', organizationId],
    queryFn: () => getLocationRecommendations(organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId,
  });
};

export const optimisationConfigQueryOptions = (organisationId: string) => {
  return queryOptions({
    queryKey: ['optimisation-config', organisationId],
    queryFn: () => getOptimisationConfig(organisationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organisationId,
  });
};

export const optimisationHistoryQueryOptions = (organisationId: string) => {
  return queryOptions({
    queryKey: ['optimisation-history', organisationId],
    queryFn: () => getOptimisationHistory(organisationId),
    staleTime: 5 * 60 * 1000,
    enabled: !!organisationId,
  });
};

export const optimisationOpportunitiesQueryOptions = (organisationId: string) => {
  return queryOptions({
    queryKey: ['optimisation-opportunities', organisationId],
    queryFn: () => getOptimisationOpportunities(organisationId),
    staleTime: 2 * 60 * 1000, // 2 minutes - more frequent refresh
    enabled: !!organisationId,
  });
};

export const useUpdateOptimisationConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['optimisation-config', 'update'],
    mutationFn: ({ organisationId, config }: { organisationId: string; config: OptimisationConfigUpdate }) =>
      updateOptimisationConfig(organisationId, config),
    onSuccess: (data, variables) => {
      // Invalidate and update the config query
      queryClient.invalidateQueries({ queryKey: ['optimisation-config', variables.organisationId] });
      queryClient.setQueryData(['optimisation-config', variables.organisationId], data);
      // Also invalidate opportunities as config changes may affect them
      queryClient.invalidateQueries({ queryKey: ['optimisation-opportunities', variables.organisationId] });
    },
    onError: (error) => {
      console.error('Error updating optimisation config:', error);
    },
  });
};

export const useApproveOptimisationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['optimisation', 'approve'],
    mutationFn: (recommendationId: string) => approveOptimisation(recommendationId),
    onSuccess: () => {
      // Invalidate opportunities to remove the approved one
      queryClient.invalidateQueries({ queryKey: ['optimisation-opportunities'] });
      // Invalidate history to show the new action log
      queryClient.invalidateQueries({ queryKey: ['optimisation-history'] });
      // Invalidate action logs to show the new action
      queryClient.invalidateQueries({ queryKey: ['actionLogs'] });
    },
    onError: (error) => {
      console.error('Error approving optimisation:', error);
    },
  });
};

export const useDismissOptimisationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['optimisation', 'dismiss'],
    mutationFn: ({ recommendationId, reason }: { recommendationId: string; reason?: string }) =>
      dismissOptimisation(recommendationId, reason),
    onSuccess: () => {
      // Invalidate opportunities to remove the dismissed one
      queryClient.invalidateQueries({ queryKey: ['optimisation-opportunities'] });
    },
    onError: (error) => {
      console.error('Error dismissing optimisation:', error);
    },
  });
};

// User Notification Settings
export const userNotificationSettingsQueryOptions = () => {
  return queryOptions<UserNotificationSettings>({
    queryKey: ['user', 'notification-settings'],
    queryFn: () => getUserNotificationSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdateUserNotificationSettingsMutation = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationKey: ['user', 'notification-settings', 'update'],
    mutationFn: (settings: UserNotificationSettingsUpdate) => updateUserNotificationSettings(settings),
    onSuccess: (data) => {
      // Update the cache with the new settings
      queryClient.setQueryData(['user', 'notification-settings'], data);
      posthog?.capture('notification_settings_updated');
    },
    onError: (error) => {
      console.error('Error updating notification settings:', error);
    },
  });
};

export const userSettingsQueryOptions = () => {
  return queryOptions<UserSettings>({
    queryKey: ['user', 'settings'],
    queryFn: () => getUserSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdateUserSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['user', 'settings', 'update'],
    mutationFn: (settings: UserSettingsUpdate) => updateUserSettings(settings),
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'settings'], data);
    },
  });
};

export const campaignHeadlineMetricsQueryOptions = (campaignId: number, days: number = 30) => {
  return queryOptions({
    queryKey: ['campaign', campaignId, 'headline-metrics', days],
    queryFn: () => getCampaignHeadlineMetrics(campaignId, days),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!campaignId,
  });
};

export const campaignDescriptionMetricsQueryOptions = (campaignId: number, days: number = 30) => {
  return queryOptions({
    queryKey: ['campaign', campaignId, 'description-metrics', days],
    queryFn: () => getCampaignDescriptionMetrics(campaignId, days),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!campaignId,
  });
};

export const externalCampaignsQueryOptions = (accountId: string) =>
  queryOptions({
    queryKey: ['external-campaigns', accountId],
    queryFn: () => fetchExternalCampaigns(accountId),
    staleTime: 5 * 60 * 1000,
    enabled: !!accountId,
  });

export const externalCampaignDetailQueryOptions = (accountId: string, campaignId: string) =>
  queryOptions({
    queryKey: ['external-campaign', accountId, campaignId],
    queryFn: () => fetchExternalCampaignDetail(accountId, campaignId),
    staleTime: 5 * 60 * 1000,
    enabled: !!accountId && !!campaignId,
  });

// ---- BusinessLocation ----------------------------------------------------

export const businessLocationsQueryOptions = (
  businessId: number,
  includeDismissed = false,
) => {
  return queryOptions({
    queryKey: ['business-locations', businessId, includeDismissed] as const,
    queryFn: () => fetchBusinessLocations(businessId, includeDismissed),
    staleTime: 30 * 1000,
    // Keep showing the previous result while a new (different queryKey)
    // fetch is in flight — prevents the brief empty flash when the user
    // toggles "Show dismissed" on/off.
    placeholderData: keepPreviousData,
  });
};

export const locationTaskStatusQueryOptions = (
  businessId: number,
  taskId: number | null,
) => {
  return queryOptions({
    queryKey: ['location-task-status', businessId, taskId] as const,
    queryFn: () => {
      if (taskId === null) throw new Error('taskId required');
      return fetchLocationTaskStatus(businessId, taskId);
    },
    enabled: taskId !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.state === 'IN_PROGRESS' ? 2000 : false;
    },
  });
};

// BusinessLocation rows also feed Business.target_locations, which the campaign wizards read
// from the org-scoped business query — invalidate that too, or the wizards seed their location
// picker from a stale business after locations change on the Business page (#4794).
const invalidateBusinessLocationConsumers = (queryClient: QueryClient, businessId: number) => {
  queryClient.invalidateQueries({ queryKey: ['business-locations', businessId] });
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'organizations' && query.queryKey[2] === 'business',
  });
};

export const useCreateLocationMutation = (businessId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BusinessLocationCreate) =>
      createBusinessLocation(businessId, payload),
    retry: 0,
    onSuccess: () => {
      invalidateBusinessLocationConsumers(queryClient, businessId);
    },
  });
};

export const useUpdateLocationMutation = (businessId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uuid,
      patch,
    }: {
      uuid: string;
      patch: BusinessLocationUpdate;
    }) => updateBusinessLocation(businessId, uuid, patch),
    retry: 0,
    onSuccess: () => {
      invalidateBusinessLocationConsumers(queryClient, businessId);
    },
  });
};

export const useDismissLocationMutation = (businessId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => dismissBusinessLocation(businessId, uuid),
    retry: 0,
    onSuccess: () => {
      invalidateBusinessLocationConsumers(queryClient, businessId);
    },
  });
};

export const useResetLocationToAgentMutation = (businessId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => resetBusinessLocationToAgent(businessId, uuid),
    retry: 0,
    onSuccess: () => {
      invalidateBusinessLocationConsumers(queryClient, businessId);
    },
  });
};

export const useRestoreLocationMutation = (businessId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => restoreBusinessLocation(businessId, uuid),
    retry: 0,
    onSuccess: () => {
      invalidateBusinessLocationConsumers(queryClient, businessId);
    },
  });
};

export const useConfirmLocationMutation = (businessId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => confirmBusinessLocation(businessId, uuid),
    retry: 0,
    onSuccess: () => {
      invalidateBusinessLocationConsumers(queryClient, businessId);
    },
  });
};

export const useTriggerDetectMutation = (businessId: number) => {
  return useMutation({
    mutationFn: () => triggerLocationDetect(businessId),
    retry: 0,
  });
};

export const useTriggerBackfillMutation = (businessId: number) => {
  // #5502: backfill is async (enqueues a Celery task). DetectorStatus polls the task and
  // invalidates the locations table + business on completion — invalidating here on the
  // 202 would be premature (the task hasn't run yet) and hit the wrong key.
  return useMutation({
    mutationFn: () => triggerLocationBackfill(businessId),
    retry: 0,
  });
};

/* Cross-platform creative-asset library (#4277).
 *
 * Signed URLs are cached on the row and stable for days (#4762), so staleTime no longer guards
 * against URL rotation. The short 60s window just bounds how stale a library page can get
 * relative to changes made elsewhere; in-tab edits update the cache directly (see below).
 */

import {
  listCreativeAssets,
  getCreativeAsset,
  uploadCreativeAsset,
  uploadCreativeAssetZip,
  deleteCreativeAsset,
  generateCreativeAssets,
  addCreativeAssetTag,
  removeCreativeAssetTag,
  setCreativeAssetPrimaryLogo,
  unsetCreativeAssetPrimaryLogo,
} from './apis';
import type { CreativeAsset, CreativeAssetGenerateParams, CreativeAssetListParams, CreativeAssetListResponse, CreativeAssetTag } from './types';

const CREATIVE_ASSETS_QUERY_ROOT = ['creative-assets'] as const;

/* Targeted creative-asset cache updates (#4762).
 *
 * Mutations used to `invalidateQueries(['creative-assets'])`, which refetched the whole library
 * (and, before the backend signed-URL cache, re-minted every asset's URL) for a one-row change.
 * Instead the mutation response is merged straight into the cache: image bytes are immutable and
 * tags are small metadata, so only the entity that changed is touched — no refetch, no URL churn,
 * and unchanged thumbnails stay byte-identical so the browser serves them from cache.
 *
 * `setQueriesData` partial-matches the `['creative-assets']` root, so one call reaches every list
 * page (keyed by its params) and every single-asset entry. The updater is shape-aware: a list
 * envelope carries `items`; a single-asset cache is a bare `CreativeAsset`.
 */
type CreativeAssetInfinite = InfiniteData<CreativeAssetListResponse>;
type CreativeAssetCacheEntry = CreativeAssetInfinite | CreativeAssetListResponse | CreativeAsset | undefined;

const isInfinite = (
  data: CreativeAssetInfinite | CreativeAssetListResponse | CreativeAsset,
): data is CreativeAssetInfinite => 'pages' in data;

const isListEnvelope = (
  data: CreativeAssetListResponse | CreativeAsset,
): data is CreativeAssetListResponse => 'items' in data;

const mapCachedAsset = (
  queryClient: QueryClient,
  assetId: number,
  transform: (asset: CreativeAsset) => CreativeAsset,
) => {
  queryClient.setQueriesData<CreativeAssetCacheEntry>({ queryKey: CREATIVE_ASSETS_QUERY_ROOT }, (data) => {
    if (!data) return data;
    if (isInfinite(data)) {
      return {
        ...data,
        pages: data.pages.map((p) => ({ ...p, items: p.items.map((a) => (a.id === assetId ? transform(a) : a)) })),
      };
    }
    if (isListEnvelope(data)) {
      return { ...data, items: data.items.map((a) => (a.id === assetId ? transform(a) : a)) };
    }
    return data.id === assetId ? transform(data) : data;
  });
};

const prependCachedAssets = (queryClient: QueryClient, assets: CreativeAsset[]) => {
  if (assets.length === 0) return;
  queryClient.setQueriesData<CreativeAssetCacheEntry>({ queryKey: CREATIVE_ASSETS_QUERY_ROOT }, (data) => {
    if (!data) return data;
    if (isInfinite(data)) {
      const existing = new Set(data.pages.flatMap((p) => p.items.map((a) => a.id)));
      const fresh = assets.filter((a) => !existing.has(a.id));
      if (fresh.length === 0) return data;
      // Prepend to the first page (newest-first) and bump `total` on every page envelope.
      return {
        ...data,
        pages: data.pages.map((p, i) => ({
          ...p,
          items: i === 0 ? [...fresh, ...p.items] : p.items,
          total: p.total + fresh.length,
        })),
      };
    }
    if (!isListEnvelope(data)) return data;
    const existing = new Set(data.items.map((a) => a.id));
    const fresh = assets.filter((a) => !existing.has(a.id));
    if (fresh.length === 0) return data;
    return { ...data, items: [...fresh, ...data.items], total: data.total + fresh.length };
  });
  assets.forEach((a) => queryClient.setQueryData([...CREATIVE_ASSETS_QUERY_ROOT, a.id], a));
};

const removeCachedAsset = (queryClient: QueryClient, assetId: number) => {
  queryClient.setQueriesData<CreativeAssetCacheEntry>({ queryKey: CREATIVE_ASSETS_QUERY_ROOT }, (data) => {
    if (!data) return data;
    if (isInfinite(data)) {
      if (!data.pages.some((p) => p.items.some((a) => a.id === assetId))) return data;
      return {
        ...data,
        pages: data.pages.map((p) => ({
          ...p,
          items: p.items.filter((a) => a.id !== assetId),
          total: Math.max(0, p.total - 1),
        })),
      };
    }
    if (!isListEnvelope(data)) return data;
    if (!data.items.some((a) => a.id === assetId)) return data;
    return { ...data, items: data.items.filter((a) => a.id !== assetId), total: Math.max(0, data.total - 1) };
  });
  queryClient.removeQueries({ queryKey: [...CREATIVE_ASSETS_QUERY_ROOT, assetId] });
};

export const creativeAssetsQueryOptions = (params: CreativeAssetListParams = {}) =>
  queryOptions({
    queryKey: [...CREATIVE_ASSETS_QUERY_ROOT, params],
    queryFn: () => listCreativeAssets(params),
    staleTime: 60 * 1000,
  });

/**
 * Infinite (offset-paginated) variant of the assets list (#5383). Backs the media library so
 * search + tag filtering happen server-side and the grid loads pages on scroll instead of pulling
 * up to 500 rows at once. `offset` is owned by the page param, so it's excluded from `params`.
 */
export const creativeAssetsInfiniteQueryOptions = (params: Omit<CreativeAssetListParams, 'offset'> = {}) => {
  const pageSize = params.limit ?? 60;
  return infiniteQueryOptions({
    queryKey: [...CREATIVE_ASSETS_QUERY_ROOT, 'infinite', { ...params, limit: pageSize }],
    queryFn: ({ pageParam }) => listCreativeAssets({ ...params, limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.offset + lastPage.items.length;
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 60 * 1000,
  });
};

export const creativeAssetQueryOptions = (assetId: number) =>
  queryOptions({
    queryKey: [...CREATIVE_ASSETS_QUERY_ROOT, assetId],
    queryFn: () => getCreativeAsset(assetId),
    staleTime: 60 * 1000,
    enabled: !!assetId,
  });

export const useUploadCreativeAssetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'upload'],
    mutationFn: (file: File) => uploadCreativeAsset(file),
    onSuccess: (asset) => {
      // The response is the full new asset — drop it straight into the library, no refetch.
      prependCachedAssets(queryClient, [asset]);
    },
  });
};

export const useUploadCreativeAssetZipMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'upload-zip'],
    mutationFn: (file: File) => uploadCreativeAssetZip(file),
    onSuccess: (response) => {
      // Prepend every imported asset; skipped entries carry no asset and are reported by the caller.
      prependCachedAssets(queryClient, response.items);
    },
  });
};

export const useGenerateCreativeAssetsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'generate'],
    mutationFn: (params: CreativeAssetGenerateParams) => generateCreativeAssets(params),
    onSuccess: (assets) => {
      // The response carries the full new rows (GET /assets item shape) — drop them
      // straight into the library caches, no refetch (#4332).
      prependCachedAssets(queryClient, assets);
    },
  });
};

export const useDeleteCreativeAssetMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'delete'],
    mutationFn: (assetId: number) => deleteCreativeAsset(assetId),
    onSuccess: (_void, assetId) => {
      removeCachedAsset(queryClient, assetId);
    },
  });
};

export const useAddCreativeAssetTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'add-tag'],
    mutationFn: ({ assetId, tagValue, tagType }: { assetId: number; tagValue: string; tagType?: 'system' | 'user' }) =>
      addCreativeAssetTag(assetId, tagValue, tagType),
    onSuccess: (newTag: CreativeAssetTag, { assetId }) => {
      // The response is the created (or existing — the endpoint is idempotent) tag. Merge it into
      // the asset's tags; no other asset is read, signed, or refetched.
      mapCachedAsset(queryClient, assetId, (asset) =>
        asset.tags.some((t) => t.id === newTag.id || t.tag_value === newTag.tag_value)
          ? asset
          : { ...asset, tags: [...asset.tags, newTag] },
      );
    },
  });
};

export const useRemoveCreativeAssetTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'remove-tag'],
    mutationFn: ({ assetId, tagValue }: { assetId: number; tagValue: string }) =>
      removeCreativeAssetTag(assetId, tagValue),
    onSuccess: (_void, { assetId, tagValue }) => {
      mapCachedAsset(queryClient, assetId, (asset) => ({
        ...asset,
        tags: asset.tags.filter((t) => t.tag_value !== tagValue),
      }));
    },
  });
};

export const useSetCreativeAssetPrimaryLogoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'set-primary-logo'],
    mutationFn: (assetId: number) => setCreativeAssetPrimaryLogo(assetId),
    onSuccess: () => {
      // Setting moves the slot off the previous holder too — refetch the whole library.
      queryClient.invalidateQueries({ queryKey: CREATIVE_ASSETS_QUERY_ROOT });
      // Org switcher shows the primary logo — bust the org list so it picks up the new URL.
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

export const useUnsetCreativeAssetPrimaryLogoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['creative-assets', 'unset-primary-logo'],
    mutationFn: (assetId: number) => unsetCreativeAssetPrimaryLogo(assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREATIVE_ASSETS_QUERY_ROOT });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

export const accountLabelsQueryOptions = () =>
  queryOptions({
    queryKey: ['labels'],
    queryFn: getAccountLabels,
    staleTime: 5 * 60 * 1000,
  });

export const adLabelsQueryOptions = (campaignId: number, adId: number | null) =>
  queryOptions({
    queryKey: ['ad-labels', campaignId, adId],
    queryFn: () => getAdLabels(campaignId, adId!),
    enabled: !!adId,
    staleTime: 60 * 1000,
  });

export const useAddAdLabelMutation = (campaignId: number, adId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (labelName: string) => addLabelToAd(campaignId, adId, labelName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ad-labels', campaignId, adId] }),
  });
};

export const useRemoveAdLabelMutation = (campaignId: number, adId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (labelId: string) => removeLabelFromAd(campaignId, adId, labelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ad-labels', campaignId, adId] }),
  });
};

export const excludedLocationsQueryOptions = (campaignId: number) =>
  queryOptions({
    queryKey: ['campaign-excluded-locations', campaignId],
    queryFn: () => getExcludedLocations(campaignId),
    staleTime: 5 * 60 * 1000,
  });

export const useAddExcludedLocationMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (criteriaId: number) => addExcludedLocation(campaignId, criteriaId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaign-excluded-locations', campaignId] }),
  });
};

export const useRemoveExcludedLocationMutation = (campaignId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (criteriaId: number) => removeExcludedLocation(campaignId, criteriaId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaign-excluded-locations', campaignId] }),
  });
};

// AI landing pages (#5701). Polls while a page is generating or a vibe edit is in flight, so the
// tab reflects async Celery progress without a manual refresh (matches the report-task poll pattern).
export const landingPageQueryOptions = (publicId: string | null, organizationId: number) => {
  return queryOptions({
    queryKey: ['landing-page', publicId],
    queryFn: () => getLandingPage(publicId as string, organizationId),
    enabled: !!publicId && !!organizationId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'GENERATING' || data.edit_in_progress)) return 3000;
      return false;
    },
  });
};

export const landingPageLeadsQueryOptions = (publicId: string | null, organizationId: number) => {
  return queryOptions({
    queryKey: ['landing-page-leads', publicId],
    queryFn: () => getLandingPageLeads(publicId as string, organizationId),
    enabled: !!publicId && !!organizationId,
  });
};

// The vibe-edit conversation transcript. Polls while an edit is in flight so the assistant's reply
// appears when the Celery task finishes (the landing-page query owns the edit_in_progress signal).
export const landingPageEditMessagesQueryOptions = (
  publicId: string | null,
  organizationId: number,
  editInProgress: boolean,
) => {
  return queryOptions({
    queryKey: ['landing-page-messages', publicId],
    queryFn: () => getLandingPageEditMessages(publicId as string, organizationId),
    enabled: !!publicId && !!organizationId,
    refetchInterval: editInProgress ? 3000 : false,
  });
};

// Resolves a campaign's existing landing page on load (survives hard reloads,
// unlike the in-memory atom). null = no page yet.
export const campaignLandingPageQueryOptions = (campaignId: number | null, organizationId: number) => {
  return queryOptions({
    queryKey: ['landing-page-by-campaign', campaignId],
    queryFn: () => getCampaignLandingPage(campaignId as number, organizationId),
    enabled: !!campaignId && !!organizationId,
  });
};

// Landing pages listed by business (#6480 decouple). Polls while any page is generating or a vibe
// edit is in flight, mirroring landingPageQueryOptions so the list reflects async Celery progress.
export const businessLandingPagesQueryOptions = (businessId: number | null, organizationId: number) => {
  return queryOptions({
    queryKey: ['landing-pages-by-business', businessId],
    queryFn: () => getBusinessLandingPages(businessId as number, organizationId),
    enabled: !!businessId && !!organizationId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.some((page) => page.status === 'GENERATING' || page.edit_in_progress)) return 3000;
      return false;
    },
  });
};

// Public API keys (#6234): the Settings page's key list.
export const apiKeysQueryOptions = (organizationId: number | undefined) => {
  return queryOptions({
    queryKey: ['api-keys', organizationId],
    queryFn: () => listApiKeys(organizationId as number),
    enabled: !!organizationId,
  });
};

const OAUTH_CONNECTIONS_QUERY_KEY = ['oauth-connections'] as const;

/** The signed-in person's connected apps (#6612). */
export const oauthConnectionsQueryOptions = () => {
  return queryOptions({
    queryKey: OAUTH_CONNECTIONS_QUERY_KEY,
    queryFn: listOauthConnections,
  });
};

/** Revoke one connected app, then refetch the list without it (#6612). */
export const useRevokeOauthConnectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['oauth-connections', 'revoke'],
    mutationFn: (connectionId: string) => revokeOauthConnection(connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OAUTH_CONNECTIONS_QUERY_KEY });
    },
  });
};

// OAuth consent screen (#6239, D11): the validated request context.
export const oauthConsentContextQueryOptions = (params: OauthConsentParams | null) => {
  return queryOptions({
    queryKey: ['oauth-consent-context', params],
    queryFn: () => getOauthConsentContext(params as OauthConsentParams),
    enabled: !!params,
    retry: false,
  });
};
